// API client — a thin fetch wrapper. No business logic; just HTTP shapes.
//
// The base URL comes from VITE_API_BASE_URL (set in console/.env.development
// for the dev server). Default to http://localhost:8000 to match `make dev-api`.
//
// Authorization: every request automatically gets `Authorization: Bearer <jwt>`
// when a token provider has been registered via `setTokenProvider`. The
// provider is set by `ApiTokenBridge` on mount, calling Clerk's
// `getToken()` to fetch a fresh session token per request. When the user
// is signed out (no provider, or provider returns null), requests go out
// without the header — the backend then 401s unless legacy bypass is on.

import type { OnboardingState } from "./onboarding";
import { tenantContext } from "./tenant";
import type {
  CustomerSummary,
  EmployeeDetail,
  EmployeeSummary,
  LeaveBalanceResponse,
  LoanEligibilityRequest,
  LoanEligibilityResponse,
  PayrollRunRequest,
  PayrollRunResponse,
  ResolutionLayerEntry,
} from "./types";

// Resolution order:
//   1. VITE_API_BASE_URL — explicit env override (Cloudflare dashboard or
//      console/.env.development).
//   2. localhost detection — running on the Vite dev server.
//   3. Railway production URL — the default for every hosted build, so
//      the workers.dev / *.solvait.com demos work without anyone
//      configuring an env var in the Cloudflare dashboard.
const _DEFAULT_PROD_API = "https://solvait-aihr-production.up.railway.app";

function _resolveApiBase(): string {
  const explicit = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (explicit) return explicit;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8000";
    }
  }
  return _DEFAULT_PROD_API;
}

const API_BASE: string = _resolveApiBase();

type TokenProvider = () => Promise<string | null>;
let _tokenProvider: TokenProvider | null = null;

export function setTokenProvider(provider: TokenProvider | null): void {
  _tokenProvider = provider;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };

  if (_tokenProvider) {
    // Race the Clerk token fetch against a 5s timeout. Without this,
    // a single network hiccup (WiFi drop, VPN flip, captive portal)
    // can leave Clerk's SDK hanging — and every fetch() call in the
    // app blocks forever waiting for the Authorization header. The
    // button click becomes a silent no-op with no error in the
    // Network tab. With the timeout, we either get the token in
    // time or proceed without it (backend 401 surfaces a clear
    // error to the user instead of an invisible hang).
    try {
      const token = await Promise.race([
        _tokenProvider(),
        new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), 5000),
        ),
      ]);
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch {
      // Token provider threw — proceed without auth header.
    }
  }

  // Defense-in-depth: when the browser is on a tenant subdomain
  // (`riyadh.solvait.com`), pass the slug along. The backend middleware
  // verifies it matches the JWT's `org_id` and 403s on mismatch. On
  // localhost or *.workers.dev the slug is null and the header isn't
  // added — Clerk JWT alone resolves the tenant.
  const tenant = tenantContext();
  if (tenant.slug) {
    headers["X-Tenant-Slug"] = tenant.slug;
  }

  const r = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (
        body &&
        typeof body.detail === "object" &&
        body.detail !== null &&
        !Array.isArray(body.detail) &&
        typeof (body.detail as { message?: string }).message === "string"
      ) {
        detail = (body.detail as { message: string }).message;
      } else if (body && typeof body.detail === "string") {
        detail = body.detail;
      } else if (Array.isArray(body?.detail)) {
        // FastAPI 422 returns a list of {loc, msg, type, input}
        // entries — flatten to a human-readable string so the
        // surface shows what's actually wrong instead of just
        // "Unprocessable Entity".
        detail = body.detail
          .map(
            (e: { loc?: unknown[]; msg?: string }) =>
              `${(e.loc ?? []).slice(1).join(".") || "body"}: ${e.msg ?? "invalid"}`,
          )
          .join("; ");
      }
    } catch {
      // body wasn't JSON — keep statusText.
    }
    throw new ApiError(r.status, detail);
  }
  // 204 No Content (or any zero-length body) → don't try r.json(),
  // it throws "Unexpected end of JSON input" and the failure looks
  // like a silent click. DELETE endpoints return 204.
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** The resolved API origin — used by non-JSON transports (SSE chat). */
export const apiBaseUrl: string = API_BASE;

/**
 * Auth + tenant headers for a request, without the JSON Content-Type.
 * Used by transports that don't go through `request()` — currently
 * the M22 chat SSE stream.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const tenant = tenantContext();
  if (tenant.slug) headers["X-Tenant-Slug"] = tenant.slug;
  return headers;
}

/**
 * Drop-in `fetch()` replacement for callers that need raw access to
 * the Response (streams, custom Content-Type, mutation flows). Adds:
 *
 *   - `apiBaseUrl` prefix (so a bare `/api/...` path hits Railway, not
 *     the same-origin Cloudflare console worker — which would
 *     otherwise serve the SPA index.html and silently break the page);
 *   - the same Clerk Bearer + X-Tenant-Slug headers the typed
 *     `request()` helper attaches.
 *
 * Caller-supplied headers win on collision.
 */
export async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const auth = await authHeaders();
  const headers = {
    ...auth,
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  return fetch(url, { ...init, headers });
}

export const api = {
  listCustomers: (): Promise<CustomerSummary[]> => request("/api/customers"),

  getCustomer: (id: string): Promise<CustomerSummary> =>
    request(`/api/customers/${id}`),

  listEmployeesForCustomer: (customerId: string): Promise<EmployeeSummary[]> =>
    request(`/api/customers/${customerId}/employees`),

  getEmployee: (id: string): Promise<EmployeeDetail> =>
    request(`/api/employees/${id}`),

  getLeaveBalance: (
    employeeId: string,
    resolutionDate?: string,
  ): Promise<LeaveBalanceResponse> => {
    const qs = resolutionDate
      ? `?resolution_date=${encodeURIComponent(resolutionDate)}`
      : "";
    return request(`/api/employees/${employeeId}/leave/balance${qs}`);
  },

  postLoanEligibility: (
    employeeId: string,
    body: LoanEligibilityRequest,
  ): Promise<LoanEligibilityResponse> =>
    request(`/api/employees/${employeeId}/loans/eligibility`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  runPayroll: (body: PayrollRunRequest): Promise<PayrollRunResponse> =>
    request("/api/payroll/run", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M7.4 onboarding wizard.
  // NOTE: this calls the M7 wizard state endpoint (still mounted
  // under /api/onboarding for backwards compatibility). The M27
  // 9-step journey state is `getOnboardingJourneyState` below.
  getOnboardingState: (): Promise<OnboardingWizardState> =>
    request("/api/setup/state"),

  upsertOnboardingGroup: (
    body: OnboardingGroupRequest,
  ): Promise<OnboardingGroupResponse> =>
    request("/api/setup/group", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M7.5 onboarding wizard — Step 2.
  listOnboardingEntities: (): Promise<OnboardingEntitiesResponse> =>
    request("/api/setup/entities"),

  // M21.5 — per-user workspace preferences (Legal Entity scope).
  getMyPreferences: (): Promise<UserPreferences> =>
    request("/api/me/preferences"),
  updateMyPreferences: (
    body: UserPreferences,
  ): Promise<UserPreferences> =>
    request("/api/me/preferences", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // M28 PR 4 — impersonation banner poll + end-session.
  getMeImpersonationActive: (): Promise<{
    active: boolean;
    staff_name: string | null;
    staff_email: string | null;
    reason: string | null;
    started_at: string | null;
    expires_at: string | null;
    token_id: string | null;
  }> => request("/api/me/impersonation-active"),

  endMeImpersonation: (): Promise<{
    ended: boolean;
    token_id: string | null;
  }> =>
    request("/api/me/impersonation-active/end", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  upsertOnboardingEntities: (
    body: OnboardingEntitiesRequest,
  ): Promise<OnboardingEntitiesResponse> =>
    request("/api/setup/entities", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M7.6 onboarding wizard — Step 3.
  getOnboardingOrgStructure: (
    legalEntityId: string,
  ): Promise<OrgStructureResponse> =>
    request(`/api/setup/org-structure/${legalEntityId}`),

  upsertOnboardingOrgStructure: (
    body: OrgStructureRequest,
  ): Promise<OrgStructureResponse> =>
    request("/api/setup/org-structure", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getSaudiCities: (): Promise<SaudiCitiesResponse> =>
    request("/api/reference/saudi-cities"),

  // M7.7 onboarding wizard — Step 6.
  previewEmployeeImport: (file: File): Promise<EmployeeImportPreview> =>
    _uploadFile("/api/setup/employees/preview", file),

  commitEmployeeImport: (file: File): Promise<EmployeeImportCommit> =>
    _uploadFile("/api/setup/employees/commit", file),

  // M7.8 onboarding wizard — Step 7.
  shadowPayroll: (
    file: File,
    periodStart: string,
    periodEnd: string,
  ): Promise<ShadowPayrollResponse> => {
    const qs = new URLSearchParams({
      period_start: periodStart,
      period_end: periodEnd,
    });
    return _uploadFile(
      `/api/setup/shadow-payroll?${qs.toString()}`,
      file,
    );
  },

  // M7.9 onboarding wizard — completion.
  completeOnboarding: (): Promise<OnboardingCompletionResponse> =>
    request("/api/setup/complete", { method: "POST" }),

  // M9.1 public signup.
  checkSignupSlug: (slug: string): Promise<SignupSlugCheck> =>
    request(`/api/signup/check-slug/${encodeURIComponent(slug)}`),

  signup: (body: SignupRequest): Promise<SignupAcceptedResponse> =>
    request("/api/signup", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  signupStatus: (tenantId: string): Promise<SignupStatusResponse> =>
    request(`/api/signup/status/${encodeURIComponent(tenantId)}`),

  // M8.5+M8.6 Integrations Console.
  listConnections: (legalEntityId: string): Promise<ConnectionsListResponse> =>
    request(
      `/api/integrations/connections?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}`,
    ),

  upsertConnection: (body: ConnectionUpsert): Promise<Connection> =>
    request("/api/integrations/connections", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteConnection: (
    integrationType: string,
    legalEntityId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/integrations/connections/${encodeURIComponent(
        integrationType,
      )}?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { method: "DELETE" },
    ),

  pingConnection: (
    integrationType: string,
    legalEntityId: string,
  ): Promise<ConnectionPing> =>
    request(
      `/api/integrations/${encodeURIComponent(
        integrationType,
      )}/ping?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),

  twilioWebhookUrl: (legalEntityId: string): Promise<{ url: string }> =>
    request(
      `/api/integrations/webhook-url/twilio/${encodeURIComponent(legalEntityId)}`,
    ),

  // M8.9 + M8.10 AI Config Console.
  listAIConfigs: (legalEntityId: string): Promise<AIConfigsResponse> =>
    request(
      `/api/ai-config?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),

  updateAIConfig: (
    agent: string,
    legalEntityId: string,
    patch: AIConfigPatch,
  ): Promise<AIConfig> =>
    request(
      `/api/ai-config/agents/${encodeURIComponent(agent)}?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { method: "PUT", body: JSON.stringify(patch) },
    ),

  getAIConfigHistory: (
    agent: string,
    legalEntityId: string,
  ): Promise<AIConfigHistoryResponse> =>
    request(
      `/api/ai-config/agents/${encodeURIComponent(agent)}/history?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),

  listKnowledge: (legalEntityId: string): Promise<KnowledgeListResponse> =>
    request(
      `/api/ai-config/knowledge?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),

  patchKnowledgeScope: (
    docId: string,
    legalEntityId: string,
    scope: string,
  ): Promise<KnowledgeDoc> =>
    request(
      `/api/ai-config/knowledge/${encodeURIComponent(docId)}?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { method: "PATCH", body: JSON.stringify({ scope }) },
    ),

  deleteKnowledge: (
    docId: string,
    legalEntityId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/ai-config/knowledge/${encodeURIComponent(docId)}?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { method: "DELETE" },
    ),

  // M11.3 employee master file.
  getEmployeeMaster: (id: string): Promise<EmployeeMasterFile> =>
    request(`/api/employees/${encodeURIComponent(id)}/master`),

  // M21.8 employee documents + current contract.
  listEmployeeDocuments: (
    employeeId: string,
  ): Promise<EmployeeDocument[]> =>
    request(`/api/employees/${encodeURIComponent(employeeId)}/documents`),
  uploadEmployeeDocument: (
    employeeId: string,
    form: FormData,
  ): Promise<EmployeeDocument> =>
    _uploadForm(
      `/api/employees/${encodeURIComponent(employeeId)}/documents`,
      form,
    ),

  /** Upload employee photo. Returns the saved blob URL.
   * Max 5 MB. Accepts JPG / PNG / WEBP / GIF. */
  uploadEmployeePhoto: (
    employeeId: string,
    file: File,
  ): Promise<{ photo_url: string }> => {
    const form = new FormData();
    form.append("file", file);
    return _uploadForm(
      `/api/employees/${encodeURIComponent(employeeId)}/photo`,
      form,
    );
  },

  /** Remove the photo (sets photo_url to null in the DB). */
  deleteEmployeePhoto: (
    employeeId: string,
  ): Promise<{ photo_url: null }> =>
    request(
      `/api/employees/${encodeURIComponent(employeeId)}/photo`,
      { method: "DELETE" },
    ),
  deleteEmployeeDocument: (
    employeeId: string,
    documentId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}`,
      { method: "DELETE" },
    ),
  employeeDocumentDownloadUrl: (
    employeeId: string,
    documentId: string,
  ): string =>
    `${API_BASE}/api/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}/download`,
  getEmployeeCurrentContract: (
    employeeId: string,
  ): Promise<EmployeeCurrentContract | null> =>
    request(
      `/api/employees/${encodeURIComponent(employeeId)}/current-contract`,
    ),

  // M11.4 employee inline edit.
  updateEmployee: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<EmployeeMasterFile> =>
    request(`/api/employees/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // M11.5 add-employee wizard.
  createEmployee: (
    body: Record<string, unknown>,
  ): Promise<EmployeeMasterFile> =>
    request("/api/employees", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M11.6 transfer workflow.
  transferEmployee: (
    id: string,
    body: {
      new_position_id: string;
      effective_date: string;
      reason_code?: string;
      notes?: string;
    },
  ): Promise<EmployeeMasterFile> =>
    request(`/api/employees/${encodeURIComponent(id)}/transfer`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M12.6 probation tracking.
  getProbationEndingSoon: (
    legalEntityId: string,
    days = 30,
  ): Promise<ProbationEndingSoonResponse> =>
    request(
      `/api/contracts/probation-ending-soon?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}`,
    ),

  // M15 leave types.
  listLeaveTypes: (
    legalEntityId: string,
    filters: { category?: string; includeInactive?: boolean } = {},
  ): Promise<LeaveTypeDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.category) qs.append("category", filters.category);
    if (filters.includeInactive) qs.append("include_inactive", "true");
    return request(`/api/leave/types?${qs.toString()}`);
  },
  getLeaveType: (id: string): Promise<LeaveTypeDetail> =>
    request(`/api/leave/types/${encodeURIComponent(id)}`),
  createLeaveType: (body: Record<string, unknown>): Promise<LeaveTypeDetail> =>
    request("/api/leave/types", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateLeaveType: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<LeaveTypeDetail> =>
    request(`/api/leave/types/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteLeaveType: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/leave/types/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M15.8 leave requests.
  listLeaveRequests: (
    filters: {
      employeeId?: string;
      legalEntityId?: string;
      state?: string;
      approverEmployeeId?: string;
    } = {},
  ): Promise<LeaveRequestDetail[]> => {
    const qs = new URLSearchParams();
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.legalEntityId) qs.append("legal_entity_id", filters.legalEntityId);
    if (filters.state) qs.append("state", filters.state);
    if (filters.approverEmployeeId)
      qs.append("approver_employee_id", filters.approverEmployeeId);
    const q = qs.toString();
    return request(`/api/leave/requests${q ? `?${q}` : ""}`);
  },
  getLeaveRequest: (id: string): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}`),
  createLeaveRequest: (
    body: Record<string, unknown>,
    actor?: string,
  ): Promise<LeaveRequestDetail> => {
    const qs = actor ? `?actor=${encodeURIComponent(actor)}` : "";
    return request(`/api/leave/requests${qs}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  approveLeaveRequest: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  denyLeaveRequest: (
    id: string,
    body: { actor?: string; reason: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/deny`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelLeaveRequest: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getLeaveRequestHistory: (id: string): Promise<WorkflowEventEntry[]> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/history`),

  // M3X — leave request mid-flight modifications (LEAVES §1.X)
  issueLeavePayInAdvance: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<{ leave_request_id: string; advance_amount: string; payroll_run_id: string | null }> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/issue-pay-in-advance`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  rescheduleLeaveRequest: (
    id: string,
    body: { new_start_date: string; new_end_date: string; actor?: string; reason?: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/reschedule`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  extendLeaveRequest: (
    id: string,
    body: { new_end_date: string; actor?: string; reason?: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/extend`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  earlyReturnLeaveRequest: (
    id: string,
    body: { actual_end_date: string; actor?: string; reason?: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/early-return`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  shortenLeaveRequest: (
    id: string,
    body: { new_end_date: string; actor?: string; reason?: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/shorten`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  transferLeaveType: (
    id: string,
    body: { new_leave_type_code: string; actor?: string; reason?: string },
  ): Promise<LeaveRequestDetail> =>
    request(`/api/leave/requests/${encodeURIComponent(id)}/transfer-type`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X — leave encashment request
  requestLeaveEncashment: (body: {
    employee_id: string;
    leave_type_code: string;
    days_to_encash: number;
    actor?: string;
    reason?: string;
  }): Promise<{
    encashment_id: string;
    amount: string;
    days_encashed: string;
    new_balance: string;
  }> =>
    request(`/api/leave/encashment/request`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M16 loan products.
  listLoanProducts: (
    legalEntityId: string,
    includeInactive?: boolean,
  ): Promise<LoanProductDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (includeInactive) qs.append("include_inactive", "true");
    return request(`/api/loans/products?${qs.toString()}`);
  },
  getLoanProduct: (id: string): Promise<LoanProductDetail> =>
    request(`/api/loans/products/${encodeURIComponent(id)}`),
  createLoanProduct: (
    body: Record<string, unknown>,
  ): Promise<LoanProductDetail> =>
    request("/api/loans/products", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateLoanProduct: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<LoanProductDetail> =>
    request(`/api/loans/products/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteLoanProduct: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/loans/products/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // M3X.LOANS PR2 — Article 90 keystone
  getArticle90Headroom: (
    employeeId: string,
    asOf?: string,
  ): Promise<{
    employee_id: string;
    as_of: string;
    monthly_basic_wage: string;
    max_allowable_monthly_deduction: string;
    existing_deductions_breakdown: {
      source: string;
      amount: string;
      rule_citation: string;
    }[];
    existing_deductions_total: string;
    headroom_remaining: string;
    utilization_percent: string;
    agent_attribution: string;
    article_citation: string;
  }> => {
    const qs = new URLSearchParams({ employee_id: employeeId });
    if (asOf) qs.append("as_of", asOf);
    return request(`/api/loans/article-90-headroom?${qs.toString()}`);
  },
  postArticle90Counterfactual: (body: {
    employee_id: string;
    requested_amount: string | number;
    requested_tenure_months: number;
    as_of?: string;
  }): Promise<{
    headroom: Record<string, unknown>;
    counterfactual: {
      requested_amount: string;
      requested_tenure_months: number;
      requested_monthly_installment: string;
      headroom_remaining: string;
      breaches: boolean;
      max_qualifying_amount: string;
      max_qualifying_tenure_months: number;
      counterfactual_message_en: string;
      counterfactual_message_ar: string;
    };
  }> =>
    request("/api/loans/article-90-counterfactual", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M16 loan applications.
  listLoanApplications: (
    filters: {
      employeeId?: string;
      legalEntityId?: string;
      state?: string;
      approverEmployeeId?: string;
    } = {},
  ): Promise<LoanApplicationDetail[]> => {
    const qs = new URLSearchParams();
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.legalEntityId)
      qs.append("legal_entity_id", filters.legalEntityId);
    if (filters.state) qs.append("state", filters.state);
    if (filters.approverEmployeeId)
      qs.append("approver_employee_id", filters.approverEmployeeId);
    const q = qs.toString();
    return request(`/api/loans/applications${q ? `?${q}` : ""}`);
  },
  getLoanApplication: (id: string): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}`),
  createLoanApplication: (
    body: Record<string, unknown>,
    actor?: string,
  ): Promise<LoanApplicationDetail> => {
    const qs = actor ? `?actor=${encodeURIComponent(actor)}` : "";
    return request(`/api/loans/applications${qs}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  runLoanEligibility: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<LoanApplicationDetail> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/run-eligibility`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  routeLoanApplication: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/route`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  approveLoanApplication: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  denyLoanApplication: (
    id: string,
    body: { actor?: string; reason: string },
  ): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/deny`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelLoanApplication: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  disburseLoanApplication: (
    id: string,
    body: { actor?: string; disbursement_date?: string } = {},
  ): Promise<LoanApplicationDetail> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/disburse`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getLoanApplicationInstallments: (
    id: string,
  ): Promise<LoanInstallmentEntry[]> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/installments`,
    ),
  getLoanApplicationHistory: (
    id: string,
  ): Promise<WorkflowEventEntry[]> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/history`),

  // M3X — payoff quote + refinance what-if (read-only previews)
  getLoanPayoffQuote: (id: string): Promise<PayoffQuoteDTO> =>
    request(`/api/loans/applications/${encodeURIComponent(id)}/payoff-quote`),

  getLoanRefinanceWhatIf: (body: {
    employee_id: string;
    tenure_options_months?: number[];
  }): Promise<RefinanceProposalDTO[]> =>
    request(`/api/loans/refinance-what-if`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X — denial explanation (bilingual + appeal path)
  getLoanDenialExplanation: (
    id: string,
  ): Promise<DenialExplanationDTO> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/denial-explanation`,
    ),

  // M3X — suspend / resume during unpaid leave
  suspendLoanForUnpaidLeave: (
    id: string,
    body: {
      leave_start: string;
      leave_end: string;
      policy_override?: string | null;
    },
  ): Promise<SuspensionDTO> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/suspend-for-unpaid-leave`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  resumeLoanAfterUnpaidLeave: (id: string): Promise<SuspensionDTO> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/resume-after-unpaid-leave`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // M3X — bond clawback trigger
  triggerBondClawback: (
    id: string,
    body: {
      bond_principal: string;
      bond_term_months: number;
      served_months: number;
      termination_cause: string;
      actor?: string;
    },
  ): Promise<{
    bond_clawback_amount: string;
    new_workflow_state: string;
    reason: string;
  }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/trigger-bond-clawback`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — refinance: proposal (1 tenure) + apply
  generateLoanRefinanceProposal: (body: {
    employee_id: string;
    new_tenure_months: number;
  }): Promise<RefinanceProposalDTO> =>
    request(`/api/loans/refinance-proposal`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  applyLoanRefinance: (body: {
    employee_id: string;
    new_tenure_months: number;
    actor?: string;
  }): Promise<{ new_loan_id: string; consolidated_loan_ids: string[] }> =>
    request(`/api/loans/apply-refinance`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X — HR override appeal (for denials)
  hrOverrideAppeal: (
    id: string,
    body: { reason: string; hr_user_id: string },
  ): Promise<{ new_workflow_state: string }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/hr-override-appeal`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — write-off (HR Director only)
  writeOffLoan: (
    id: string,
    body: {
      hr_director_user_id: string;
      months_since_default: number;
      write_off_after_months?: number;
    },
  ): Promise<{ applied: boolean; new_workflow_state: string }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/write-off`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — reschedule one installment
  rescheduleInstallment: (
    id: string,
    body: { installment_id: string; new_due_date: string; actor?: string },
  ): Promise<{
    cancelled_installment_id: string;
    new_installment_id: string;
    new_due_date: string;
    new_amount: string;
    audit_note: string;
  }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/reschedule-installment`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — cancel remaining installments (paired with write-off)
  cancelRemainingInstallments: (
    id: string,
    body: { hr_director_user_id: string; reason?: string },
  ): Promise<{ cancelled_count: number }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/cancel-remaining-installments`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — off-cycle disbursement
  disburseLoanOffCycle: (
    id: string,
    body: { disbursement_date?: string; actor?: string },
  ): Promise<LoanApplicationDetail> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/disburse-off-cycle`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X — mark loan in arrears
  markLoanArrears: (
    id: string,
  ): Promise<{ marked: boolean; new_workflow_state: string }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/mark-arrears`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // M3X — Tashghil auto-approve attempt
  tryAutoApproveLoan: (
    id: string,
  ): Promise<{
    auto_approved: boolean;
    new_workflow_state: string;
    reason: string;
  }> =>
    request(
      `/api/loans/applications/${encodeURIComponent(id)}/try-auto-approve`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // M3X — guarantor management
  proposeLoanGuarantor: (body: {
    loan_application_id: string;
    guarantor_employee_id: string;
    coverage_amount?: string;
    actor?: string;
  }): Promise<{ row_id: string; status: string }> =>
    request(`/api/loans/guarantors/propose`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  acceptLoanGuarantor: (rowId: string): Promise<{ id: string; status: string }> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/accept`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  declineLoanGuarantor: (rowId: string, body: { reason?: string } = {}): Promise<{ id: string; status: string }> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/decline`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeLoanGuarantor: (rowId: string, body: { reason?: string } = {}): Promise<{ id: string; status: string }> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/revoke`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X — Article 90 carry-forward visibility
  listLoanCarryForward: (employeeId?: string): Promise<Array<{
    id: string;
    employee_id: string;
    loan_application_id: string;
    amount: string;
    status: string;
    created_at: string;
  }>> => {
    const qs = employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : "";
    return request(`/api/loans/carry-forward${qs}`);
  },

  // M3X — court / GOSI / damaged-property garnishments
  listGarnishments: (employeeId?: string): Promise<Array<{
    id: string;
    employee_id: string;
    source: string;
    priority: number;
    amount_per_period: string;
    status: string;
    reference: string | null;
  }>> => {
    const qs = employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : "";
    return request(`/api/loans/garnishments${qs}`);
  },
  createGarnishment: (body: {
    employee_id: string;
    source: string;
    amount_per_period: string;
    reference?: string;
    priority?: number;
  }): Promise<{ id: string }> =>
    request(`/api/loans/garnishments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelGarnishment: (id: string, body: { reason?: string } = {}): Promise<{ cancelled: boolean }> =>
    request(`/api/loans/garnishments/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M14 allowances.
  listAllowances: (
    legalEntityId: string,
    filters: { category?: string; allowanceType?: string; includeInactive?: boolean } = {},
  ): Promise<AllowanceDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.category) qs.append("category", filters.category);
    if (filters.allowanceType) qs.append("allowance_type", filters.allowanceType);
    if (filters.includeInactive) qs.append("include_inactive", "true");
    return request(`/api/allowances?${qs.toString()}`);
  },
  getAllowance: (id: string): Promise<AllowanceDetail> =>
    request(`/api/allowances/${encodeURIComponent(id)}`),
  createAllowance: (body: Record<string, unknown>): Promise<AllowanceDetail> =>
    request("/api/allowances", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAllowance: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<AllowanceDetail> =>
    request(`/api/allowances/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteAllowance: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/allowances/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getAllowanceUsage: (
    id: string,
  ): Promise<{ grade_count: number; position_count: number; contract_count: number }> =>
    request(`/api/allowances/${encodeURIComponent(id)}/usage`),

  // M14.4 3-layer assignment endpoints (Grade / Position / Contract).
  listGradeAllowances: (gradeId: string): Promise<AllowanceAssignment[]> =>
    request(`/api/grades/${encodeURIComponent(gradeId)}/allowances`),
  upsertGradeAllowance: (
    gradeId: string,
    body: { allowance_id: string; amount_override: string | null },
  ): Promise<AllowanceAssignment> =>
    request(`/api/grades/${encodeURIComponent(gradeId)}/allowances`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteGradeAllowance: (
    gradeId: string,
    allowanceId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/grades/${encodeURIComponent(gradeId)}/allowances/${encodeURIComponent(allowanceId)}`,
      { method: "DELETE" },
    ),
  listPositionAllowances: (positionId: string): Promise<AllowanceAssignment[]> =>
    request(`/api/positions/${encodeURIComponent(positionId)}/allowances`),
  upsertPositionAllowance: (
    positionId: string,
    body: { allowance_id: string; amount_override: string | null },
  ): Promise<AllowanceAssignment> =>
    request(`/api/positions/${encodeURIComponent(positionId)}/allowances`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deletePositionAllowance: (
    positionId: string,
    allowanceId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/positions/${encodeURIComponent(positionId)}/allowances/${encodeURIComponent(allowanceId)}`,
      { method: "DELETE" },
    ),
  listContractAllowances: (contractId: string): Promise<AllowanceAssignment[]> =>
    request(`/api/contracts/${encodeURIComponent(contractId)}/allowances`),
  upsertContractAllowance: (
    contractId: string,
    body: { allowance_id: string; amount_override: string | null },
  ): Promise<AllowanceAssignment> =>
    request(`/api/contracts/${encodeURIComponent(contractId)}/allowances`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteContractAllowance: (
    contractId: string,
    allowanceId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/contracts/${encodeURIComponent(contractId)}/allowances/${encodeURIComponent(allowanceId)}`,
      { method: "DELETE" },
    ),

  // M13 grades.
  listGrades: (legalEntityId: string): Promise<GradeDetail[]> =>
    request(
      `/api/grades?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  getGrade: (id: string): Promise<GradeDetail> =>
    request(`/api/grades/${encodeURIComponent(id)}`),
  getGradeResolved: (id: string): Promise<GradeResolved> =>
    request(`/api/grades/${encodeURIComponent(id)}/resolved`),
  createGrade: (body: Record<string, unknown>): Promise<GradeDetail> =>
    request("/api/grades", { method: "POST", body: JSON.stringify(body) }),
  updateGrade: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<GradeDetail> =>
    request(`/api/grades/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteGrade: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/grades/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M3X.COMP F.3 — comp band catalog editor.
  listCompBands: (
    legalEntityId: string,
    gradeId?: string,
  ): Promise<CompBandDTO[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (gradeId) qs.set("grade_id", gradeId);
    return request(`/api/comp-bands?${qs.toString()}`);
  },
  getCompBand: (id: string): Promise<CompBandDTO> =>
    request(`/api/comp-bands/${encodeURIComponent(id)}`),
  createCompBand: (body: Record<string, unknown>): Promise<CompBandDTO> =>
    request("/api/comp-bands", { method: "POST", body: JSON.stringify(body) }),
  updateCompBand: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<CompBandDTO> =>
    request(`/api/comp-bands/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCompBand: (id: string): Promise<{ deactivated: boolean }> =>
    request(`/api/comp-bands/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M3X.COMP F.4 — commission structures editor.
  listCommissionStructures: (
    legalEntityId: string,
  ): Promise<CommissionStructureDTO[]> =>
    request(
      `/api/commission-structures?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  createCommissionStructure: (
    body: Record<string, unknown>,
  ): Promise<CommissionStructureDTO> =>
    request("/api/commission-structures", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCommissionStructure: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<CommissionStructureDTO> =>
    request(`/api/commission-structures/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCommissionStructure: (
    id: string,
  ): Promise<{ deactivated: boolean }> =>
    request(`/api/commission-structures/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // M3X.COMP F.5 + F.6 — comp_changes workflow.
  listCompChanges: (params: {
    employee_id?: string;
    legal_entity_id?: string;
    workflow_state?: string;
  }): Promise<CompChangeDTO[]> => {
    const qs = new URLSearchParams();
    if (params.employee_id) qs.set("employee_id", params.employee_id);
    if (params.legal_entity_id)
      qs.set("legal_entity_id", params.legal_entity_id);
    if (params.workflow_state)
      qs.set("workflow_state", params.workflow_state);
    return request(`/api/comp-changes?${qs.toString()}`);
  },
  getCompChange: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}`),
  createCompChange: (body: Record<string, unknown>): Promise<CompChangeDTO> =>
    request("/api/comp-changes", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  compChangeSubmit: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/submit`, {
      method: "POST",
    }),
  compChangeRouteForApproval: (id: string): Promise<CompChangeDTO> =>
    request(
      `/api/comp-changes/${encodeURIComponent(id)}/route-for-approval`,
      { method: "POST" },
    ),
  compChangeApprove: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    }),
  compChangeActivate: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/activate`, {
      method: "POST",
    }),
  compChangeClose: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/close`, {
      method: "POST",
    }),
  compChangeReject: (id: string, reason: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  compChangeCancel: (id: string): Promise<CompChangeDTO> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    }),
  compChangeRequestConsent: (
    id: string,
    locale: "en" | "ar",
  ): Promise<{
    notification_id: string;
    channel: string;
    status: string;
    subject: string;
  }> =>
    request(
      `/api/comp-changes/${encodeURIComponent(id)}/article-60-request-consent`,
      { method: "POST", body: JSON.stringify({ locale }) },
    ),
  compChangeUploadConsent: (
    id: string,
    body: { consent_date: string; consent_pdf_url: string },
  ): Promise<CompChangeDTO> =>
    request(
      `/api/comp-changes/${encodeURIComponent(id)}/article-60-consent`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  compChangeHistory: (id: string): Promise<WorkflowEventDTO[]> =>
    request(`/api/comp-changes/${encodeURIComponent(id)}/history`),

  // M3X.COMP F.C — per-employee comp history aggregator.
  getEmployeeCompHistory: (
    employeeId: string,
  ): Promise<EmployeeCompHistory> =>
    request(
      `/api/people/employees/${encodeURIComponent(employeeId)}/comp-history`,
    ),

  // M3X.COMP F.17 — annual merit cycle.
  listCompCycles: (legalEntityId: string): Promise<CompCycleDTO[]> =>
    request(
      `/api/comp-cycles?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  getCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}`),
  createCompCycle: (body: Record<string, unknown>): Promise<CompCycleDTO> =>
    request("/api/comp-cycles", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listCompCycleEntries: (id: string): Promise<CompCycleEntryDTO[]> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/entries`),
  setCompCycleMultiplier: (
    cycleId: string,
    employeeId: string,
    multiplier: string,
  ): Promise<CompCycleEntryDTO> =>
    request(
      `/api/comp-cycles/${encodeURIComponent(cycleId)}/entries/${encodeURIComponent(employeeId)}/multiplier`,
      { method: "PUT", body: JSON.stringify({ multiplier }) },
    ),
  modelCompCycle: (id: string): Promise<CompCycleEntryDTO[]> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/model`, {
      method: "POST",
    }),
  narrateCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/narrate`, {
      method: "POST",
    }),
  approveCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    }),
  flipEffectiveCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(
      `/api/comp-cycles/${encodeURIComponent(id)}/effective-date-arrived`,
      { method: "POST" },
    ),
  postCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/post`, {
      method: "POST",
    }),
  cancelCompCycle: (id: string): Promise<CompCycleDTO> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    }),
  getCompCycleBudget: (
    id: string,
  ): Promise<Record<string, string | boolean>> =>
    request(`/api/comp-cycles/${encodeURIComponent(id)}/budget`),

  // M12.7 contract detail page.
  getContract: (id: string): Promise<ContractDetail> =>
    request(`/api/contracts/${encodeURIComponent(id)}`),

  // M27 (real) PR 1 — Onboarding journey state.
  // NOTE: these MUST be /api/onboarding/state (the M27 9-step
  // journey path) — NOT /api/setup/state (the M7 pre-tenant
  // wizard path). A regex rename in PR 1 accidentally caught
  // these too; corrected here.
  getOnboardingJourneyState: (): Promise<OnboardingState> =>
    request("/api/onboarding/state"),

  aiJobMetadataFromTitle: (body: {
    title_en: string;
    legal_entity_id: string;
    context?: string;
  }): Promise<{
    code: string;
    title_ar: string;
    job_family: string;
    is_supervisory: boolean;
  }> =>
    request("/api/ai-assist/job-metadata", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  aiJobFullProfile: (body: {
    title_en: string;
    legal_entity_id: string;
    context?: string;
  }): Promise<AiJobFullProfile> =>
    request("/api/ai-assist/job-full-profile", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ============ M29 Import Framework (Phase F) =====================
  /** PR 1 ships only POST + GET + PUT session. The full surface
   * (auto-map, validate, commit, pending tray, mapping templates,
   * AI fill / correct / suggest) lands in PRs 2-10. */
  importCreateSession: (
    files: File[],
    opts?: { legalEntityId?: string | null },
  ): Promise<ImportSessionDTO> => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    if (opts?.legalEntityId) fd.append("legal_entity_id", opts.legalEntityId);
    return _uploadForm("/api/imports/sessions", fd);
  },

  importGetSession: (sessionId: string): Promise<ImportSessionDTO> =>
    request(`/api/imports/sessions/${encodeURIComponent(sessionId)}`),

  importListSessions: (): Promise<ImportSessionDTO[]> =>
    request("/api/imports/sessions"),

  importPatchSession: (
    sessionId: string,
    body: { legal_entity_id?: string | null },
  ): Promise<ImportSessionDTO> =>
    request(`/api/imports/sessions/${encodeURIComponent(sessionId)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ---- M29 PR 3-5: Understanding / Clarification / Proposals -----
  importUnderstand: (
    sessionId: string,
    opts?: { skipAi?: boolean },
  ): Promise<Record<string, unknown>> => {
    const qs = opts?.skipAi ? "?skip_ai=true" : "";
    return request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/understand${qs}`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  /** The new universal "ingest + ready" endpoint. Synchronous,
   * fast (~3s), no AI. Works from any session state. Replaces
   * the async Understanding orchestrator. */
  importProcess: (sessionId: string): Promise<{
    rows_ingested: number;
    sheets_processed: number;
    valid: number;
    warning: number;
    error: number;
  }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/process`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  importDataBrief: (sessionId: string): Promise<Record<string, unknown>> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/data-brief`,
    ),

  /** List ingested ImportRows with their validation messages.
   * Default filter is status=error so HR can see what's wrong. */
  importListRows: (
    sessionId: string,
    opts?: { status?: "valid" | "warning" | "error" | "all"; limit?: number },
  ): Promise<{
    session_id: string;
    filter_status: string;
    returned: number;
    totals: Record<string, number>;
    rows: Array<{
      id: string;
      entity_type: string;
      source_file_index: number;
      source_sheet_name: string | null;
      source_row_index: number;
      status: string;
      source_data: Record<string, unknown>;
      mapped_data: Record<string, unknown>;
      validation_messages: Array<{
        level: string;
        message_en: string;
        message_ar?: string;
        field?: string;
      }>;
      update_kind: "create" | "update" | "unchanged" | null;
      field_changes: Record<string, { old: string | null; new: string }>;
      matched_entity_id: string | null;
    }>;
  }> => {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", opts.status);
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString() ? `?${params}` : "";
    return request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/rows${qs}`,
    );
  },

  importClarify: (sessionId: string): Promise<{
    questions: Array<Record<string, unknown>>;
    ai_available: boolean;
  }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/clarify`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  importClarifyAnswer: (
    sessionId: string,
    body: { question_index: number; answer_text: string },
  ): Promise<Record<string, unknown>> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/clarify/answer`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  importClarifyState: (sessionId: string): Promise<{
    questions: Array<Record<string, unknown>>;
    answers: Array<Record<string, unknown>>;
    conversation_id: string | null;
  }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/clarify`,
    ),

  importSkipClarification: (sessionId: string): Promise<{ status: string }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/clarify/skip`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  importSeedProposals: (sessionId: string): Promise<Record<string, number>> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/proposals/seed`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  importListProposals: (sessionId: string): Promise<ImportProposalDTO[]> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/proposals`,
    ),

  importApproveProposal: (
    proposalId: string,
    body: { edited_payload?: Record<string, unknown> | null } = {},
  ): Promise<ImportProposalDTO> =>
    request(
      `/api/imports/proposals/${encodeURIComponent(proposalId)}/approve`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  importRejectProposal: (
    proposalId: string,
    body: { reason?: string | null } = {},
  ): Promise<ImportProposalDTO> =>
    request(
      `/api/imports/proposals/${encodeURIComponent(proposalId)}/reject`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  importBulkApproveProposals: (
    sessionId: string,
    body: { kind?: string | null; min_confidence?: number } = {},
  ): Promise<{ approved: number }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/proposals/bulk-approve`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  importCommitGate: (sessionId: string): Promise<{
    allowed: boolean;
    reason: string | null;
  }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/proposals/commit-gate`,
    ),

  // ---- M29 PR 8: Commit + pending tray ---------------------------
  importCommit: (
    sessionId: string,
    body: { create_contract_draft?: boolean; seed_leave_balances?: boolean } = {},
  ): Promise<{
    accepted?: boolean;
    already_completed?: boolean;
    session_id?: string;
    status?: string;
    counts?: Record<string, number>;
  }> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/commit`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  importListSessionPending: (
    sessionId: string,
  ): Promise<ImportPendingRowDTO[]> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/pending`,
    ),

  importListAllPending: (): Promise<ImportPendingRowDTO[]> =>
    request("/api/imports/pending"),

  importFixPending: (
    pendingId: string,
    body: { mapped_data: Record<string, unknown>; legal_entity_id: string },
  ): Promise<{
    committed: boolean;
    status: string;
    validation_messages: Array<Record<string, unknown>>;
  }> =>
    request(
      `/api/imports/pending/${encodeURIComponent(pendingId)}/fix`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  importDiscardPending: (pendingId: string): Promise<void> =>
    request(
      `/api/imports/pending/${encodeURIComponent(pendingId)}`,
      { method: "DELETE" },
    ),

  // ---- M29 PR 9: Failure explanations -----------------------------
  importListFailures: (
    sessionId: string,
  ): Promise<ImportFailureDTO[]> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/failures`,
    ),

  importCreateFailure: (
    sessionId: string,
    body: {
      failure_kind: string;
      affected_file_index?: number | null;
      affected_sheet_name?: string | null;
      context_summary?: string | null;
    },
  ): Promise<ImportFailureDTO> =>
    request(
      `/api/imports/sessions/${encodeURIComponent(sessionId)}/failures`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  seedKsaDefaults: (): Promise<Record<string, Record<string, number>>> =>
    request("/api/onboarding/seed-defaults", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  prepareUploadPolicyPath: (): Promise<{
    deactivated: Record<string, number>;
    blocked: Array<{ domain: string; code: string; reason: string }>;
  }> =>
    request("/api/onboarding/policies/prepare-upload-path", {
      method: "POST",
      body: JSON.stringify({}),
    }),

  getPolicyPromotionStatus: (): Promise<{
    has_promoted_items: boolean;
    promoted_total: number;
  }> => request("/api/onboarding/policies/promotion-status"),

  updateOnboardingJourneyState: (
    body: {
      current_step?: string | null;
      dismissed_until?: string | null;
      go_live_at?: string | null;
    },
  ): Promise<OnboardingState> =>
    request("/api/onboarding/state", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  updateOnboardingStep: (
    slug: string,
    body: {
      status?: "not_started" | "in_progress" | "completed" | "skipped";
      skipped_reason?: string | null;
      data?: Record<string, unknown>;
    },
  ): Promise<OnboardingState> =>
    request(`/api/onboarding/state/steps/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // M27 PR 3 — Dashboards index + Billing usage.
  getDashboardsIndex: (
    legalEntityId?: string | null,
  ): Promise<{ dashboards: DashboardSummary[] }> => {
    const q = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/reports/dashboards/index${q}`);
  },

  getBillingUsage: (): Promise<BillingUsage> =>
    request("/api/settings/billing/usage"),

  // M27 PR 2 — Legal Entities CRUD.
  listLegalEntities: (
    params: { includeArchived?: boolean; search?: string | null } = {},
  ): Promise<LegalEntityListItem[]> => {
    const q = new URLSearchParams();
    if (params.includeArchived) q.set("include_archived", "true");
    if (params.search) q.set("search", params.search);
    const qs = q.toString();
    return request(`/api/legal-entities${qs ? `?${qs}` : ""}`);
  },

  getLegalEntity: (id: string): Promise<LegalEntityDetail> =>
    request(`/api/legal-entities/${encodeURIComponent(id)}`),

  createLegalEntity: (
    body: LegalEntityCreateBody,
  ): Promise<LegalEntityDetail> =>
    request("/api/legal-entities", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateLegalEntity: (
    id: string,
    body: Partial<LegalEntityCreateBody>,
  ): Promise<LegalEntityDetail> =>
    request(`/api/legal-entities/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  archiveLegalEntity: (id: string): Promise<{ archived: boolean }> =>
    request(`/api/legal-entities/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // M27 PR 1 — Tenant Settings.
  getTenantSettings: (): Promise<TenantSettings> =>
    request("/api/settings/tenant"),

  updateTenantSettings: (
    patch: Partial<TenantSettingsEditable>,
  ): Promise<TenantSettings> =>
    request("/api/settings/tenant", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // Users & Roles (Step 2 in-app user management) -----------------------
  listTenantMembers: (): Promise<TenantMember[]> =>
    request("/api/settings/users"),

  getTenantMember: (userId: string): Promise<TenantMemberDetail> =>
    request(`/api/settings/users/${encodeURIComponent(userId)}`),

  inviteTenantMember: (body: {
    email: string;
    role: TenantRole;
  }): Promise<TenantInvitation> =>
    request("/api/settings/users/invite", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  changeTenantMemberRole: (
    userId: string,
    newRole: TenantRole,
  ): Promise<TenantMember> =>
    request(`/api/settings/users/${encodeURIComponent(userId)}/role`, {
      method: "PUT",
      body: JSON.stringify({ new_role: newRole }),
    }),

  removeTenantMember: (userId: string): Promise<void> =>
    request(`/api/settings/users/${encodeURIComponent(userId)}`, {
      method: "DELETE",
    }),

  suspendTenantMember: (
    userId: string,
    reason?: string,
  ): Promise<{ status: string }> =>
    request(`/api/settings/users/${encodeURIComponent(userId)}/suspend`, {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    }),

  unsuspendTenantMember: (userId: string): Promise<{ status: string }> =>
    request(`/api/settings/users/${encodeURIComponent(userId)}/unsuspend`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  listTenantInvitations: (): Promise<TenantInvitation[]> =>
    request("/api/settings/invitations"),

  revokeTenantInvitation: (invitationId: string): Promise<void> =>
    request(
      `/api/settings/invitations/${encodeURIComponent(invitationId)}`,
      { method: "DELETE" },
    ),

  // M26 PR 5 — Holiday Calendar.
  listHolidays: (
    params: {
      legalEntityId?: string | null;
      year?: number;
      source?: "statutory" | "company" | null;
    } = {},
  ): Promise<HolidayItem[]> => {
    const q = new URLSearchParams();
    if (params.legalEntityId) q.set("legal_entity_id", params.legalEntityId);
    if (params.year !== undefined) q.set("year", String(params.year));
    if (params.source) q.set("source", params.source);
    const qs = q.toString();
    return request(`/api/holidays${qs ? `?${qs}` : ""}`);
  },

  createHoliday: (body: {
    legal_entity_id: string;
    name_en: string;
    name_ar: string;
    observed_date: string;
    recurrence: "annual" | "one_time";
    note?: string | null;
  }): Promise<HolidayItem> =>
    request("/api/holidays", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteHoliday: (id: string): Promise<void> =>
    request(`/api/holidays/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // M26 PR 3 contracts list.
  listContracts: (
    params: {
      legalEntityId?: string | null;
      status?: string | null;
      contractType?: string | null;
      employeeSearch?: string | null;
      probationOnly?: boolean;
    } = {},
  ): Promise<ContractsListItem[]> => {
    const q = new URLSearchParams();
    if (params.legalEntityId) q.set("legal_entity_id", params.legalEntityId);
    if (params.status) q.set("status", params.status);
    if (params.contractType) q.set("contract_type", params.contractType);
    if (params.employeeSearch)
      q.set("employee_search", params.employeeSearch);
    if (params.probationOnly) q.set("probation_only", "true");
    const qs = q.toString();
    return request(`/api/contracts${qs ? `?${qs}` : ""}`);
  },

  updateContract: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<ContractDetail> =>
    request(`/api/contracts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // M11.7 terminate workflow (M21.9: auto-triggers M18 EOS).
  terminateEmployee: (
    id: string,
    body: {
      last_working_day: string;
      termination_reason: string;
      reason_code?: "with_cause" | "without_cause" | "mutual";
      notice_period_served?: boolean;
      notes?: string;
    },
  ): Promise<EmployeeMasterFile> =>
    request(`/api/employees/${encodeURIComponent(id)}/terminate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M10.5 org chart.
  getOrgChart: (
    legalEntityId: string,
    hierarchyType: string = "primary",
  ): Promise<OrgChartResponse> =>
    request(
      `/api/org-chart?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}&hierarchy_type=${encodeURIComponent(hierarchyType)}`,
    ),

  // M10.6 Position CRUD.
  listJobs: (legalEntityId: string): Promise<JobSummary[]> =>
    request(
      `/api/jobs?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),

  listPositions: (
    legalEntityId: string,
    filters: { status?: string; jobId?: string; q?: string } = {},
  ): Promise<PositionListItem[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.status) qs.append("status", filters.status);
    if (filters.jobId) qs.append("job_id", filters.jobId);
    if (filters.q) qs.append("q", filters.q);
    return request(`/api/positions?${qs.toString()}`);
  },

  /** Generic employee lookup (powers <EmployeePicker />). Substring
   * matches against employee_code + first/last names in EN + AR. */
  listEmployeesForPicker: (opts?: {
    q?: string;
    legalEntityId?: string;
    includeTerminated?: boolean;
    limit?: number;
  }): Promise<EmployeePickerOption[]> => {
    const qs = new URLSearchParams();
    if (opts?.q) qs.set("q", opts.q);
    if (opts?.legalEntityId) qs.set("legal_entity_id", opts.legalEntityId);
    if (opts?.includeTerminated)
      qs.set("include_terminated", "true");
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const s = qs.toString();
    return request(`/api/employees${s ? `?${s}` : ""}`);
  },

  getPosition: (id: string): Promise<PositionDetail> =>
    request(`/api/positions/${encodeURIComponent(id)}`),

  createPosition: (body: PositionCreateBody): Promise<PositionDetail> =>
    request("/api/positions", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updatePosition: (
    id: string,
    body: PositionUpdateBody,
  ): Promise<PositionDetail> =>
    request(`/api/positions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  abolishPosition: (id: string): Promise<PositionDetail> =>
    request(`/api/positions/${encodeURIComponent(id)}/abolish`, {
      method: "POST",
    }),

  // M21.6 Position assignment history + reports-to.
  positionAssignmentHistory: (
    id: string,
  ): Promise<AssignmentHistoryItem[]> =>
    request(
      `/api/positions/${encodeURIComponent(id)}/assignment-history`,
    ),
  positionReportsTo: (id: string): Promise<ReportsToEdge[]> =>
    request(`/api/positions/${encodeURIComponent(id)}/reports-to`),
  setPositionReportsTo: (
    id: string,
    body: { reports_to_position_id: string; hierarchy_type_code?: string },
  ): Promise<ReportsToEdge[]> =>
    request(`/api/positions/${encodeURIComponent(id)}/reports-to`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // M10.7 Job CRUD.
  getJob: (id: string): Promise<JobSummary> =>
    request(`/api/jobs/${encodeURIComponent(id)}`),

  createJob: (body: JobCreateBody): Promise<JobSummary> =>
    request("/api/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateJob: (id: string, body: JobUpdateBody): Promise<JobSummary> =>
    request(`/api/jobs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteJob: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M21.7 Job competencies (skills / KPIs / certificates / tests / objectives).
  listJobCompetencies: (
    jobId: string,
    kind?: string,
  ): Promise<JobCompetency[]> => {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : "";
    return request(
      `/api/jobs/${encodeURIComponent(jobId)}/competencies${qs}`,
    );
  },
  createJobCompetency: (
    jobId: string,
    body: Partial<JobCompetency> & { kind: string; name_en: string },
  ): Promise<JobCompetency> =>
    request(`/api/jobs/${encodeURIComponent(jobId)}/competencies`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateJobCompetency: (
    competencyId: string,
    body: Partial<JobCompetency>,
  ): Promise<JobCompetency> =>
    request(
      `/api/jobs/competencies/${encodeURIComponent(competencyId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  deleteJobCompetency: (
    competencyId: string,
  ): Promise<{ deleted: boolean }> =>
    request(
      `/api/jobs/competencies/${encodeURIComponent(competencyId)}`,
      { method: "DELETE" },
    ),
  // M21.7 AI-generated job description (a DRAFT — HR reviews + saves).
  generateJobDescription: (
    jobId: string,
  ): Promise<{ draft: string; prompt_version: number; model: string }> =>
    request(
      `/api/jobs/${encodeURIComponent(jobId)}/generate-description`,
      { method: "POST" },
    ),

  // Department CRUD (post-onboarding edit surface).
  listDepartments: (legalEntityId: string): Promise<DepartmentSummary[]> =>
    request(
      `/api/legal-entities/${encodeURIComponent(legalEntityId)}/departments`,
    ),
  getDepartment: (id: string): Promise<DepartmentSummary> =>
    request(`/api/departments/${encodeURIComponent(id)}`),
  createDepartment: (
    legalEntityId: string,
    body: { name_en: string; name_ar: string; parent_department_id?: string | null },
  ): Promise<DepartmentSummary> =>
    request(
      `/api/legal-entities/${encodeURIComponent(legalEntityId)}/departments`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateDepartment: (
    id: string,
    body: Partial<{ name_en: string; name_ar: string; parent_department_id: string | null }>,
  ): Promise<DepartmentSummary> =>
    request(`/api/departments/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteDepartment: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/departments/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // Branch CRUD (post-onboarding edit surface).
  listBranches: (legalEntityId: string): Promise<BranchSummary[]> =>
    request(
      `/api/legal-entities/${encodeURIComponent(legalEntityId)}/branches`,
    ),
  getBranch: (id: string): Promise<BranchSummary> =>
    request(`/api/branches/${encodeURIComponent(id)}`),
  createBranch: (
    legalEntityId: string,
    body: { name_en: string; name_ar: string; city?: string | null },
  ): Promise<BranchSummary> =>
    request(
      `/api/legal-entities/${encodeURIComponent(legalEntityId)}/branches`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateBranch: (
    id: string,
    body: Partial<{ name_en: string; name_ar: string; city: string | null }>,
  ): Promise<BranchSummary> =>
    request(`/api/branches/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteBranch: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/branches/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M17.1 shifts (catalog + assignment).
  listShifts: (
    legalEntityId: string,
    includeInactive?: boolean,
  ): Promise<ShiftDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (includeInactive) qs.append("include_inactive", "true");
    return request(`/api/shifts?${qs.toString()}`);
  },
  getShift: (id: string): Promise<ShiftDetail> =>
    request(`/api/shifts/${encodeURIComponent(id)}`),
  createShift: (body: Record<string, unknown>): Promise<ShiftDetail> =>
    request("/api/shifts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateShift: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<ShiftDetail> =>
    request(`/api/shifts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteShift: (id: string): Promise<{ deleted: boolean }> =>
    request(`/api/shifts/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // M17.2 attendance.
  listAttendance: (filters: {
    employeeId?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}): Promise<AttendanceDetail[]> => {
    const qs = new URLSearchParams();
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.dateFrom) qs.append("date_from", filters.dateFrom);
    if (filters.dateTo) qs.append("date_to", filters.dateTo);
    const q = qs.toString();
    return request(`/api/attendance${q ? `?${q}` : ""}`);
  },

  // M17.6 excuse types.
  listExcuseTypes: (
    legalEntityId: string,
    opts: { category?: string; includeInactive?: boolean } = {},
  ): Promise<ExcuseTypeDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (opts.category) qs.append("category", opts.category);
    if (opts.includeInactive) qs.append("include_inactive", "true");
    return request(`/api/excuse-types?${qs.toString()}`);
  },
  getExcuseType: (id: string): Promise<ExcuseTypeDetail> =>
    request(`/api/excuse-types/${encodeURIComponent(id)}`),
  createExcuseType: (
    body: Record<string, unknown>,
  ): Promise<ExcuseTypeDetail> =>
    request("/api/excuse-types", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateExcuseType: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<ExcuseTypeDetail> =>
    request(`/api/excuse-types/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // M17.7-17.9 excuse requests.
  listExcuseRequests: (
    filters: {
      employeeId?: string;
      legalEntityId?: string;
      state?: string;
      approverEmployeeId?: string;
    } = {},
  ): Promise<ExcuseRequestDetail[]> => {
    const qs = new URLSearchParams();
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.legalEntityId)
      qs.append("legal_entity_id", filters.legalEntityId);
    if (filters.state) qs.append("state", filters.state);
    if (filters.approverEmployeeId)
      qs.append("approver_employee_id", filters.approverEmployeeId);
    const q = qs.toString();
    return request(`/api/excuse-requests${q ? `?${q}` : ""}`);
  },
  getExcuseRequest: (id: string): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}`),
  createExcuseRequest: (
    body: Record<string, unknown>,
    actor?: string,
  ): Promise<ExcuseRequestDetail> => {
    const qs = actor ? `?actor=${encodeURIComponent(actor)}` : "";
    return request(`/api/excuse-requests${qs}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  routeExcuseRequest: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/route`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  approveExcuseRequest: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  denyExcuseRequest: (
    id: string,
    body: { actor?: string; reason: string },
  ): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/deny`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelExcuseRequest: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  recordExcuseRequest: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<ExcuseRequestDetail> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/record`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getExcuseRequestHistory: (
    id: string,
  ): Promise<WorkflowEventEntry[]> =>
    request(`/api/excuse-requests/${encodeURIComponent(id)}/history`),

  // M18.8 termination workflow.
  listTerminations: (
    filters: {
      legalEntityId?: string;
      state?: string;
      employeeId?: string;
    } = {},
  ): Promise<TerminationSummary[]> => {
    const qs = new URLSearchParams();
    if (filters.legalEntityId)
      qs.append("legal_entity_id", filters.legalEntityId);
    if (filters.state) qs.append("state", filters.state);
    if (filters.employeeId)
      qs.append("employee_id", filters.employeeId);
    const q = qs.toString();
    return request(`/api/terminations${q ? `?${q}` : ""}`);
  },
  getTermination: (id: string): Promise<TerminationDetail> =>
    request(`/api/terminations/${encodeURIComponent(id)}`),
  initiateResignation: (
    body: Record<string, unknown>,
    actor?: string,
  ): Promise<TerminationSummary> => {
    const qs = actor ? `?actor=${encodeURIComponent(actor)}` : "";
    return request(`/api/terminations/resign${qs}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  initiateTerminationHr: (
    body: Record<string, unknown>,
    actor?: string,
  ): Promise<TerminationSummary> => {
    const qs = actor ? `?actor=${encodeURIComponent(actor)}` : "";
    return request(`/api/terminations/initiate${qs}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  routeTermination: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/route`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  approveTermination: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  denyTermination: (
    id: string,
    body: { actor?: string; reason: string },
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/deny`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelTermination: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  beginExitClearance: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/begin-clearance`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  draftFilings: (id: string): Promise<GovernmentFilingDetail[]> =>
    request(`/api/terminations/${encodeURIComponent(id)}/draft-filings`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  markFilingsDrafted: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/mark-filings-drafted`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  generateFinalPayslip: (
    id: string,
    body: { actor?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/generate-final-payslip`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  closeTermination: (
    id: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<TerminationSummary> =>
    request(`/api/terminations/${encodeURIComponent(id)}/close`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getTerminationHistory: (
    id: string,
  ): Promise<WorkflowEventEntry[]> =>
    request(`/api/terminations/${encodeURIComponent(id)}/history`),
  getExitClearance: (id: string): Promise<ExitClearanceFull> =>
    request(`/api/terminations/${encodeURIComponent(id)}/clearance`),
  completeClearanceItem: (
    termId: string,
    itemId: string,
    body: { actor?: string; reason?: string } = {},
  ): Promise<ExitClearanceItem> =>
    request(
      `/api/terminations/${encodeURIComponent(termId)}/clearance/items/${encodeURIComponent(itemId)}/complete`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  waiveClearanceItem: (
    termId: string,
    itemId: string,
    body: { actor?: string; reason: string },
  ): Promise<ExitClearanceItem> =>
    request(
      `/api/terminations/${encodeURIComponent(termId)}/clearance/items/${encodeURIComponent(itemId)}/waive`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  listTerminationFilings: (id: string): Promise<GovernmentFilingDetail[]> =>
    request(`/api/terminations/${encodeURIComponent(id)}/filings`),

  // M20.1 Mission Control headlines aggregator.
  missionControlHeadlines: (
    legalEntityId?: string,
  ): Promise<MissionControlHeadlines> => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/mission-control/headlines${qs}`);
  },
  // M20.2 Pending approvals widget (cross-workflow).
  missionControlPendingApprovals: (
    filters: {
      legalEntityId?: string;
      approverEmployeeId?: string;
      limitPerType?: number;
    } = {},
  ): Promise<PendingApprovalItem[]> => {
    const qs = new URLSearchParams();
    if (filters.legalEntityId)
      qs.append("legal_entity_id", filters.legalEntityId);
    if (filters.approverEmployeeId)
      qs.append("approver_employee_id", filters.approverEmployeeId);
    if (filters.limitPerType !== undefined)
      qs.append("limit_per_type", String(filters.limitPerType));
    const q = qs.toString();
    return request(
      `/api/mission-control/pending-approvals${q ? `?${q}` : ""}`,
    );
  },
  // M20.3 Compliance alerts widget.
  missionControlComplianceAlerts: (
    legalEntityId?: string,
  ): Promise<ComplianceAlertsResponse> => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/mission-control/compliance-alerts${qs}`);
  },
  // M20.4 Anomalies widget.
  missionControlAnomalies: (
    legalEntityId?: string,
  ): Promise<AnomaliesResponse> => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/mission-control/anomalies${qs}`);
  },
  // M20.4 AI activity log.
  missionControlAiActivity: (
    legalEntityId?: string,
    windowHours = 24,
  ): Promise<AiActivityResponse> => {
    const qs = new URLSearchParams();
    if (legalEntityId)
      qs.append("legal_entity_id", legalEntityId);
    qs.append("window_hours", String(windowHours));
    return request(
      `/api/mission-control/ai-activity?${qs.toString()}`,
    );
  },
  // Active approvers — drives the "Acting as" picker.
  missionControlActiveApprovers: (
    legalEntityId?: string,
  ): Promise<ActiveApproversResponse> => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/mission-control/active-approvers${qs}`);
  },

  // M30 P8 — HR Operations Hub aggregator. Returns the 7-card grid
  // + recent HR-initiated activity feed + pending PayrollRunCandidate
  // count. Drives the /operations/hub landing page (M30.5.1).
  operationsHub: (): Promise<HubAggregate> =>
    request("/api/operations/hub"),

  // M30.5.1 — PayrollRunCandidate queue list view. Defaults to
  // status='queued' newest-first. Pass statusFilter='all' to see
  // every status.
  listPayrollCandidates: (filters: {
    statusFilter?: string;
    targetPeriod?: string;
    limit?: number;
  } = {}): Promise<PayrollCandidatesListResponse> => {
    const qs = new URLSearchParams();
    if (filters.statusFilter)
      qs.append("status_filter", filters.statusFilter);
    if (filters.targetPeriod)
      qs.append("target_period", filters.targetPeriod);
    if (filters.limit !== undefined)
      qs.append("limit", String(filters.limit));
    const q = qs.toString();
    return request(
      `/api/operations/payroll-candidates${q ? `?${q}` : ""}`,
    );
  },

  // M30.5.3 — list reason codes for a transaction type. Backed by
  // the YAML loader (rules/ksa/reason_codes/<type>.yaml).
  listReasonCodes: (
    transactionType: string,
  ): Promise<ReasonCodesListResponse> =>
    request(
      `/api/reason-codes/${encodeURIComponent(transactionType)}`,
    ),

  // M30.5.3 — Leave HR-initiate (assist + commit).
  leaveHrInitiateAssist: (
    body: LeaveHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request("/api/transactions/leave/hr-initiate/assist", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  leaveHrInitiateCommit: (
    body: LeaveHrInitiateBody,
  ): Promise<LeaveHrInitiateCommitOut> =>
    request("/api/transactions/leave/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M30.5.3 — Loan HR-initiate.
  loanHrInitiateAssist: (
    body: LoanHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request("/api/transactions/loan/hr-initiate/assist", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  loanHrInitiateCommit: (
    body: LoanHrInitiateBody,
  ): Promise<LoanHrInitiateCommitOut> =>
    request("/api/transactions/loan/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M30.5.3 — Excuse HR-initiate.
  excuseHrInitiateAssist: (
    body: ExcuseHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request("/api/transactions/excuse/hr-initiate/assist", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  excuseHrInitiateCommit: (
    body: ExcuseHrInitiateBody,
  ): Promise<ExcuseHrInitiateCommitOut> =>
    request("/api/transactions/excuse/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M30.5.4 — T&A HR-initiate.
  attendanceHrInitiateAssist: (
    body: AttendanceHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request("/api/transactions/attendance/hr-initiate/assist", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  attendanceHrInitiateCommit: (
    body: AttendanceHrInitiateBody,
  ): Promise<AttendanceHrInitiateCommitOut> =>
    request("/api/transactions/attendance/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M30.5.4 — Overtime HR-initiate.
  overtimeHrInitiateAssist: (
    body: OvertimeHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request("/api/transactions/overtime/hr-initiate/assist", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  overtimeHrInitiateCommit: (
    body: OvertimeHrInitiateBody,
  ): Promise<OvertimeHrInitiateCommitOut> =>
    request("/api/transactions/overtime/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M30.5.4 — Contract Amendment HR-initiate.
  contractAmendmentHrInitiateAssist: (
    body: ContractAmendmentHrInitiateBody,
  ): Promise<TransactionAssistOut> =>
    request(
      "/api/transactions/contract-amendment/hr-initiate/assist",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  contractAmendmentHrInitiateCommit: (
    body: ContractAmendmentHrInitiateBody,
  ): Promise<{ contract_id: string }> =>
    request("/api/transactions/contract-amendment/hr-initiate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M23.2 Chat drafts — "Save as draft" + the review surface.
  saveChatDraft: (body: {
    kind: string;
    title: string;
    content: string;
    session_id?: string | null;
  }): Promise<ChatDraft> =>
    request("/api/chat/save-draft", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listChatDrafts: (
    status?: "pending" | "accepted" | "discarded",
  ): Promise<ChatDraft[]> =>
    request(`/api/chat/drafts${status ? `?status=${status}` : ""}`),
  acceptChatDraft: (id: string): Promise<ChatDraft> =>
    request(`/api/chat/drafts/${encodeURIComponent(id)}/accept`, {
      method: "POST",
    }),
  discardChatDraft: (id: string): Promise<ChatDraft> =>
    request(`/api/chat/drafts/${encodeURIComponent(id)}/discard`, {
      method: "POST",
    }),

  // M24 Chat confirmations — Apply / Cancel a parked chat mutation.
  applyChatConfirmation: (
    confirmationId: string,
    conversationSnippet?: string | null,
  ): Promise<ChatConfirmResult> =>
    request("/api/chat/confirm", {
      method: "POST",
      body: JSON.stringify({
        confirmation_id: confirmationId,
        conversation_snippet: conversationSnippet ?? null,
      }),
    }),
  cancelChatConfirmation: (
    confirmationId: string,
  ): Promise<{ confirmation_id: string; status: string }> =>
    request(
      `/api/chat/confirm/${encodeURIComponent(confirmationId)}/cancel`,
      { method: "POST" },
    ),

  // Chat feedback (👍 / 👎 + optional comment) — see the
  // MessageActions component and /ai/feedback page.
  postChatFeedback: (body: {
    session_id: string;
    turn_id: string;
    sentiment: "up" | "down";
    comment?: string | null;
    message_snippet?: string | null;
  }): Promise<ChatFeedback> =>
    request("/api/chat/feedback", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listChatFeedback: (params: {
    limit?: number;
    sentiment?: "up" | "down" | null;
  } = {}): Promise<ChatFeedbackListResponse> => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.sentiment) q.set("sentiment", params.sentiment);
    const suffix = q.toString() ? `?${q}` : "";
    return request(`/api/chat/feedback${suffix}`);
  },
  deleteChatFeedback: (id: string): Promise<void> =>
    request(`/api/chat/feedback/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // M25.3 Chat threads — pin / list / open / rename / unpin.
  saveChatThread: (body: {
    name: string;
    transcript: Record<string, unknown>[];
    session_id?: string | null;
  }): Promise<ChatThread> =>
    request("/api/chat/threads", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listChatThreads: (): Promise<ChatThreadSummary[]> =>
    request("/api/chat/threads"),
  getChatThread: (id: string): Promise<ChatThread> =>
    request(`/api/chat/threads/${encodeURIComponent(id)}`),
  renameChatThread: (id: string, name: string): Promise<ChatThread> =>
    request(`/api/chat/threads/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  deleteChatThread: (id: string): Promise<void> =>
    request(`/api/chat/threads/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  shareChatThread: (id: string): Promise<ChatThread> =>
    request(`/api/chat/threads/${encodeURIComponent(id)}/share`, {
      method: "POST",
    }),
  unshareChatThread: (id: string): Promise<ChatThread> =>
    request(`/api/chat/threads/${encodeURIComponent(id)}/unshare`, {
      method: "POST",
    }),
  getSharedThread: (code: string): Promise<SharedThread> =>
    request(`/api/chat/shared/${encodeURIComponent(code)}`),

  // M25.5 Suggested follow-ups — a best-effort Haiku call after each
  // assistant turn. Always resolves (no key / error → empty list).
  chatFollowUps: (body: {
    user_message: string;
    assistant_message: string;
    locale: string;
    legal_entity_id?: string | null;
  }): Promise<{ follow_ups: string[] }> =>
    request("/api/chat/follow-ups", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M21.10 AI Status — top-bar status dot + popover.
  getAiStatus: (): Promise<AiStatus> => request("/api/ai-status"),
  pauseAi: (): Promise<AiStatus> =>
    request("/api/ai-status/pause", { method: "POST" }),
  resumeAi: (): Promise<AiStatus> =>
    request("/api/ai-status/resume", { method: "POST" }),
  setAutonomyMode: (
    mode: "conservative" | "standard" | "aggressive",
  ): Promise<AiStatus> =>
    request("/api/ai-status/autonomy-mode", {
      method: "PUT",
      body: JSON.stringify({ mode }),
    }),

  // M21.10 Global Search — ⌘K command palette.
  globalSearch: (q: string): Promise<GlobalSearchResponse> =>
    request(`/api/search?q=${encodeURIComponent(q)}`),

  // M21.15 Report dashboards.
  getHeadcountReport: (le?: string): Promise<HeadcountReport> =>
    request(
      `/api/reports/headcount${le ? `?legal_entity_id=${le}` : ""}`,
    ),
  getTurnoverReport: (le?: string): Promise<TurnoverReport> =>
    request(
      `/api/reports/turnover${le ? `?legal_entity_id=${le}` : ""}`,
    ),
  getCompensationReport: (le?: string): Promise<CompensationReport> =>
    request(
      `/api/reports/compensation${le ? `?legal_entity_id=${le}` : ""}`,
    ),
  getComplianceReport: (le?: string): Promise<ComplianceReport> =>
    request(
      `/api/reports/compliance${le ? `?legal_entity_id=${le}` : ""}`,
    ),

  // M3X.LOANS §1.15 — guarantor exposure + per-employee row list
  getGuarantorExposure: (
    employeeId: string,
  ): Promise<{
    guarantor_employee_id: string;
    total_liability: string;
    active_count: number;
    by_loan: unknown[];
  }> =>
    request(
      `/api/loans/guarantors/exposure?guarantor_employee_id=${encodeURIComponent(employeeId)}`,
    ),

  listGuarantorRowsForEmployee: (employeeId: string): Promise<unknown[]> =>
    request(
      `/api/loans/guarantors/by-employee?guarantor_employee_id=${encodeURIComponent(employeeId)}`,
    ),

  acceptGuarantor: (rowId: string): Promise<unknown> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/accept`, {
      method: "POST",
    }),

  declineGuarantor: (rowId: string, reason: string): Promise<unknown> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/decline`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),

  revokeGuarantor: (rowId: string): Promise<unknown> =>
    request(`/api/loans/guarantors/${encodeURIComponent(rowId)}/revoke`, {
      method: "POST",
    }),

  // M3X.LEAVES §1.X — the 30 leave-domain reports
  getLeaveReports: (
    le?: string,
  ): Promise<
    Array<{
      name: string;
      headline: string;
      headline_ar: string;
      agent_attribution: string;
      data: Record<string, unknown>;
      metrics: Record<string, unknown>;
    }>
  > =>
    request(
      `/api/reports/leaves${le ? `?legal_entity_id=${le}` : ""}`,
    ),

  // M3X.EOS F.22 — 30 EOS-domain reports
  getEosReports: (
    le?: string,
  ): Promise<
    Array<{
      name: string;
      headline: string;
      headline_ar: string;
      agent_attribution: string;
      data: Record<string, unknown>;
      metrics: Record<string, unknown>;
    }>
  > =>
    request(
      `/api/reports/eos${le ? `?legal_entity_id=${le}` : ""}`,
    ),

  // M3X.LOANS §1.18 — the 25 loan-domain reports
  getLoanReports: (
    le?: string,
  ): Promise<
    Array<{
      name: string;
      headline: string;
      headline_ar: string;
      agent_attribution: string;
      data: Record<string, unknown>;
      metrics: Record<string, unknown>;
    }>
  > =>
    request(
      `/api/reports/loans${le ? `?legal_entity_id=${le}` : ""}`,
    ),

  // M3X.EOS F.9 — Article 81 sick-leave / maternity protection
  checkArticle81Protection: (
    employeeId: string,
    checkDate?: string,
  ): Promise<{
    is_protected: boolean;
    veto_reason: string | null;
    article_citation: string | null;
    estimated_return_date: string | null;
    protected_categories: string[];
  }> => {
    const qs = new URLSearchParams({
      employee_id: employeeId,
    });
    if (checkDate) qs.append("check_date", checkDate);
    return request(`/api/eos/article-81/check?${qs.toString()}`);
  },

  // M3X.ATTENDANCE F.3 — shift rotation patterns
  listShiftRotationPatterns: (
    legalEntityId?: string,
  ): Promise<
    Array<{
      id: string;
      legal_entity_id: string;
      code: string;
      name_en: string;
      name_ar: string | null;
      description: string | null;
      steps: Array<{ shift_id: string; day_count: number }>;
      cycle_length_days: number;
    }>
  > => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/shift-rotation/patterns${qs}`);
  },
  createShiftRotationPattern: (body: {
    legal_entity_id: string;
    code: string;
    name_en: string;
    name_ar?: string | null;
    description?: string | null;
    steps: Array<{ shift_id: string; day_count: number }>;
  }) =>
    request(`/api/shift-rotation/patterns`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  assignShiftRotation: (body: {
    position_id: string;
    pattern_id: string;
    effective_from: string;
    notes?: string | null;
  }) =>
    request(`/api/shift-rotation/assignments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  resolveShiftForDate: (
    positionId: string,
    onDate: string,
  ): Promise<{ position_id: string; on_date: string; shift_id: string | null }> =>
    request(
      `/api/shift-rotation/resolve?position_id=${encodeURIComponent(
        positionId,
      )}&on_date=${encodeURIComponent(onDate)}`,
    ),

  // M3X.ATTENDANCE F.23 KEYSTONE — Article 107 OT premium calc
  getOtFloorMultipliers: (): Promise<Record<string, string>> =>
    request(`/api/ot/floor-multipliers`),
  computeOt: (body: {
    ot_type: string;
    hours: string;
    basic_salary: string;
    customer_multiplier?: string | null;
    employee_gender?: string;
    employee_is_juvenile?: boolean;
    is_exempt_industry?: boolean;
    friday_consent_recorded?: boolean;
    working_days_per_month?: number;
    scheduled_hours_per_day?: number;
    currency?: string;
    cumulative_weekly_ot_so_far?: string;
  }): Promise<{
    hours: string;
    basic_hourly: string;
    multiplier_applied: string;
    statutory_floor_multiplier: string;
    amount: string;
    currency: string;
    article_citation: string;
    audit_trace: Record<string, unknown>;
  }> =>
    request(`/api/ot/compute`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.ATTENDANCE F.11 — late detection + Article 100 rest
  detectLate: (body: {
    shift_id: string;
    clock_in_time: string;
    autonomy_mode?: string;
  }): Promise<{
    is_late: boolean;
    late_minutes: number;
    scheduled_start: string;
    grace_minutes: number;
    article_citation: string | null;
    khidmat_outreach_recommended: boolean;
    outreach_reason: string | null;
  }> =>
    request(`/api/attendance/detect-late`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  checkArticle100Rest: (body: {
    worked_minutes_since_last_break: number;
    break_duration_minutes_taken: number;
  }): Promise<{
    violation: boolean;
    violation_minutes: number;
    article_citation: string;
    description: string;
  }> =>
    request(`/api/attendance/check-article-100-rest`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.ATTENDANCE F.6 — biometric machines
  listBiometricMachines: (
    legalEntityId?: string,
  ): Promise<BiometricMachineDTO[]> => {
    const qs = legalEntityId
      ? `?legal_entity_id=${encodeURIComponent(legalEntityId)}`
      : "";
    return request(`/api/biometric/machines${qs}`);
  },
  createBiometricMachine: (body: {
    legal_entity_id: string;
    machine_id: string;
    device_type?: string;
    location_label?: string | null;
    connection_mode?: string;
    sync_interval_minutes?: number;
  }): Promise<BiometricMachineDTO> =>
    request(`/api/biometric/machines`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  syncBiometricMachine: (
    id: string,
  ): Promise<{
    machine_id: string;
    punches_received: number;
    success: boolean;
    machine_status: string;
    error_detail: string | null;
  }> =>
    request(`/api/biometric/machines/${encodeURIComponent(id)}/sync`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // M3X.ATTENDANCE F.2 — shift validation
  validateShiftDefinition: (
    shiftId: string,
  ): Promise<{
    ok: boolean;
    shift_id: string;
    crosses_midnight: boolean;
    overlaps_night_window: boolean;
  }> => request(`/api/shifts/${encodeURIComponent(shiftId)}/validate-definition`),
  validateShiftAssignment: (
    shiftId: string,
    params: {
      employee_id: string;
      legal_entity_industry?: string;
      hr_director_acknowledged?: boolean;
    },
  ): Promise<{
    can_assign: boolean;
    violations: string[];
    warnings: string[];
    article_citations: string[];
  }> => {
    const qs = new URLSearchParams({ employee_id: params.employee_id });
    if (params.legal_entity_industry)
      qs.append("legal_entity_industry", params.legal_entity_industry);
    if (params.hr_director_acknowledged)
      qs.append("hr_director_acknowledged", "true");
    return request(
      `/api/shifts/${encodeURIComponent(shiftId)}/validate-assignment?${qs.toString()}`,
    );
  },

  // M3X.EOS F.3 — Notice-period sub-workflow
  initNoticePeriod: (body: {
    termination_id: string;
    notice_start_date: string;
    last_working_day: string;
  }): Promise<NoticePeriodDTO> =>
    request(`/api/eos/notice-period/init`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  fireNoticePeriodTransition: (
    noticePeriodId: string,
    body: { to_state: string; actor?: string; payload?: unknown },
  ): Promise<NoticePeriodDTO> =>
    request(
      `/api/eos/notice-period/${encodeURIComponent(noticePeriodId)}/transition`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  getNoticePeriodForTermination: (
    terminationId: string,
  ): Promise<NoticePeriodDTO> =>
    request(
      `/api/eos/notice-period/by-termination/${encodeURIComponent(terminationId)}`,
    ),

  // M3X.EOS F.2 — Wire deceased/retirement/absconded triggers into M18
  initiateDeceasedTermination: (body: {
    employee_id: string;
    legal_entity_id: string;
    date_of_death: string;
    death_certificate_url?: string | null;
  }): Promise<unknown> =>
    request(`/api/terminations/initiate-deceased`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  initiateRetirementTermination: (body: {
    employee_id: string;
    legal_entity_id: string;
    retirement_date: string;
    auto_triggered?: boolean;
  }): Promise<unknown> =>
    request(`/api/terminations/initiate-retirement`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  initiateAbscondedTermination: (body: {
    employee_id: string;
    legal_entity_id: string;
    last_seen_date: string;
    all_five_conditions_acknowledged: boolean;
  }): Promise<unknown> =>
    request(`/api/terminations/initiate-absconded`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.EOS F.11 — Deceased workflow
  allocateDeceasedSettlement: (body: {
    total_settlement: string;
    beneficiaries: Array<{
      beneficiary_id: string;
      name_en: string;
      name_ar: string;
      kinship: string;
      national_id?: string | null;
      contact_phone?: string | null;
      contact_email?: string | null;
    }>;
    currency?: string;
  }): Promise<unknown> =>
    request(`/api/eos/deceased/allocate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  renderDeceasedCondolence: (body: {
    deceased_name_en: string;
    deceased_name_ar: string;
    legal_entity_name: string;
    locale?: string;
  }): Promise<{ subject: string; body: string }> =>
    request(`/api/eos/deceased/condolence-message`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.EOS F.8 — Article 80(7) absconded 5-condition checklist
  getAbscondedConditions: (): Promise<
    Array<{
      code: string;
      label_en: string;
      label_ar: string;
      auto_populatable_from: string | null;
      evidence_type: string;
    }>
  > => request(`/api/eos/absconded/conditions`),
  validateAbscondedAcknowledgments: (
    acknowledgments: Array<{
      condition_code: string;
      acknowledged: boolean;
      actor_user_id: string;
      actor_name: string;
      notes: string;
      evidence_url: string | null;
    }>,
  ): Promise<{
    all_acknowledged: boolean;
    missing_conditions: string[];
    can_zero_gratuity: boolean;
    veto_reason: string | null;
  }> =>
    request(`/api/eos/absconded/validate`, {
      method: "POST",
      body: JSON.stringify({ acknowledgments }),
    }),

  // M3X.EOS F.10 — Retirement scanner
  getRetirementCandidates: (params: {
    legal_entity_id?: string;
    horizon_days?: number;
    age_male?: number;
    age_female?: number;
  }): Promise<{
    candidates: Array<{
      employee_id: string;
      employee_code: string;
      name_en: string;
      name_ar: string;
      date_of_birth: string;
      gender: string;
      retirement_age: number;
      estimated_retirement_date: string;
      days_until_retirement: number;
      workflow_state: string;
    }>;
    total_count: number;
    by_window_days: Record<string, number>;
    horizon_days: number;
  }> => {
    const qs = new URLSearchParams();
    if (params.legal_entity_id) qs.append("legal_entity_id", params.legal_entity_id);
    qs.append("horizon_days", String(params.horizon_days ?? 180));
    if (params.age_male) qs.append("age_male", String(params.age_male));
    if (params.age_female) qs.append("age_female", String(params.age_female));
    return request(`/api/eos/retirement/candidates?${qs.toString()}`);
  },
  getRetirementPlanningPacket: (
    employeeId: string,
    locale: "en" | "ar" = "en",
  ): Promise<{
    employee_id: string;
    candidate: Record<string, unknown>;
    packet: { subject: string; body: string };
    locale: string;
  }> =>
    request(
      `/api/eos/retirement/planning-packet/${encodeURIComponent(employeeId)}?locale=${locale}`,
    ),

  // M3X.EOS F.4 + F.5 — Exit interview synthesis
  getExitInterviewQuestions: (): Promise<
    Array<{
      code: string;
      category: string;
      prompt_en: string;
      prompt_ar: string;
    }>
  > => request(`/api/eos/exit-interview/questions`),
  synthesizeExitInterview: (body: {
    responses: Array<{
      question_code: string;
      response_text: string;
      response_rating?: number | null;
    }>;
    employee_role?: string;
    service_years?: number;
    legal_entity_name?: string;
    locale?: string;
  }): Promise<ExitInterviewSynthesisDTO> =>
    request(`/api/eos/exit-interview/synthesize`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.EOS F.21 — Onboarding auto-flush
  runEosOnboardingAutoflush: (body: {
    legal_entity_id: string;
    proposed_cells: Array<{
      trigger_type: string;
      tier_label: string;
      min_years: number;
      max_years: number;
      percent: string;
      extractor_confidence: string;
      source_quote?: string;
    }>;
    auto_commit_threshold?: string;
    jurisdiction?: string;
  }): Promise<EosAutoFlushResultDTO> =>
    request(`/api/eos/onboarding/run-autoflush`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  runEosOnboardingDemo: (body: {
    legal_entity_id: string;
    auto_commit_threshold?: string;
  }): Promise<EosAutoFlushResultDTO> =>
    request(`/api/eos/onboarding/run-demo`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.EOS F.6 — Article 80 classification (Sonnet + deterministic fallback)
  classifyArticle80: (
    narrative: string,
  ): Promise<Article80ClassifyResult> =>
    request(`/api/eos/article-80/classify`, {
      method: "POST",
      body: JSON.stringify({ narrative }),
    }),
  listArticle80Subtypes: (): Promise<Article80Subtype[]> =>
    request(`/api/eos/article-80/subtypes`),
  getArticle80Evidence: (
    subtypeCode: string,
  ): Promise<{
    subtype_code: string;
    subtype: Article80Subtype;
    evidence_required: string[];
  }> =>
    request(
      `/api/eos/article-80/evidence?subtype_code=${encodeURIComponent(subtypeCode)}`,
    ),

  // M3X.EOS F.15 — Preview the 12-component settlement breakdown
  previewEosSettlement: (body: {
    employee_id: string;
    legal_entity_id: string;
    trigger_type: string;
    last_actual_wage: string;
    hire_date: string;
    exit_date: string;
    last_payroll_close: string;
    leave_encashment_amount?: string;
    ticket_entitlement_value?: string;
    notice_days_paid_in_lieu?: number;
    notice_served?: boolean;
    pending_allowances?: string;
    death_uplift_months?: number;
    outstanding_loan_balance?: string;
    unpaid_violations_amount?: string;
    pending_deductions?: string;
    schooling_deduct_amount?: string;
    thirteenth_salary_deduct_amount?: string;
    pay_trans_deduct_amount?: string;
    currency?: string;
  }): Promise<SettlementBreakdownDTO> =>
    request(`/api/eos/settlement/preview`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M3X.EOS §1.2 — Per-LE rate matrix (customer-policy above Article 84/85 floor)
  listEosRateMatrix: (
    legalEntityId: string,
  ): Promise<EosRateMatrixCellDTO[]> =>
    request(
      `/api/eos/rate-matrix?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  upsertEosRateMatrixCell: (body: {
    legal_entity_id: string;
    trigger_type: string;
    tier_label: string;
    min_years: number;
    max_years: number;
    percent: string;
    jurisdiction?: string;
  }): Promise<EosRateMatrixCellDTO> =>
    request(`/api/eos/rate-matrix/upsert`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getEosRateMatrixFloor: (params: {
    trigger_type: string;
    min_years: number;
    max_years: number;
    jurisdiction?: string;
  }): Promise<EosFloorRefDTO> => {
    const qs = new URLSearchParams({
      trigger_type: params.trigger_type,
      min_years: String(params.min_years),
      max_years: String(params.max_years),
      jurisdiction: params.jurisdiction ?? "ksa",
    });
    return request(`/api/eos/rate-matrix/floor?${qs.toString()}`);
  },
  seedEosRateMatrixDefault: (
    legalEntityId: string,
  ): Promise<{ seeded_count: number; legal_entity_id: string }> =>
    request(
      `/api/eos/rate-matrix/seed-default?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  // M21.15 Audit log viewer.
  getAuditLog: (params: {
    source?: string;
    actor?: string;
    eventType?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogResponse> => {
    const qs = new URLSearchParams();
    if (params.source && params.source !== "all")
      qs.append("source", params.source);
    if (params.actor) qs.append("actor", params.actor);
    if (params.eventType) qs.append("event_type", params.eventType);
    qs.append("limit", String(params.limit ?? 50));
    qs.append("offset", String(params.offset ?? 0));
    return request(`/api/audit-log?${qs.toString()}`);
  },

  // M24.5 — undo a reversible chat mutation (24h window).
  undoChatMutation: (
    auditEventId: string,
  ): Promise<{
    audit_event_id: string;
    tool_name: string;
    reversed: boolean;
  }> =>
    request("/api/chat/undo", {
      method: "POST",
      body: JSON.stringify({ audit_event_id: auditEventId }),
    }),

  // M21.14 Saudi statutory-defaults reference.
  getStatutoryDefaults: (
    domain: "leave" | "loan" | "allowance" | "grade",
  ): Promise<StatutoryDefaultsResponse> =>
    request(`/api/statutory-defaults/${domain}`),

  // M21.13 Approval Chain Configurator.
  listApprovalChains: (
    legalEntityId?: string,
    workflowType?: string,
  ): Promise<ApprovalChain[]> => {
    const qs = new URLSearchParams();
    if (legalEntityId) qs.append("legal_entity_id", legalEntityId);
    if (workflowType) qs.append("workflow_type", workflowType);
    const q = qs.toString();
    return request(`/api/approval-chains${q ? `?${q}` : ""}`);
  },
  getApprovalChain: (id: string): Promise<ApprovalChain> =>
    request(`/api/approval-chains/${encodeURIComponent(id)}`),
  upsertApprovalChain: (body: {
    legal_entity_id: string;
    workflow_type: string;
    steps: ApprovalChainStep[];
    notes?: string | null;
  }): Promise<ApprovalChain> =>
    request("/api/approval-chains", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  resolveApprovalChainPreview: (
    legalEntityId: string,
    workflowType: string,
    employeeId: string,
  ): Promise<ResolvedApprovalStep[]> =>
    request(
      "/api/approval-chains/resolved/preview" +
        `?legal_entity_id=${encodeURIComponent(legalEntityId)}` +
        `&workflow_type=${encodeURIComponent(workflowType)}` +
        `&employee_id=${encodeURIComponent(employeeId)}`,
    ),

  // M21.11 AI Assist — translate + generate (DRAFT-only, commandment 23).
  aiTranslate: (body: {
    text: string;
    direction: "en_to_ar" | "ar_to_en";
    legal_entity_id: string;
    context_hint?: string;
  }): Promise<AiTranslateResponse> =>
    request("/api/ai-assist/translate", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  aiGenerate: (body: {
    kind: "job_description" | "termination_reason" | "leave_request_reason";
    legal_entity_id: string;
    context: Record<string, string>;
  }): Promise<AiGenerateResponse> =>
    request("/api/ai-assist/generate", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // M19.6 AI policy ingestion.
  uploadPolicyPdf: async (
    legalEntityId: string,
    file: File,
    actor?: string,
  ): Promise<PolicyExtractionDraftDetail> => {
    const form = new FormData();
    form.append("legal_entity_id", legalEntityId);
    form.append("file", file);
    if (actor) form.append("actor", actor);
    const headers: Record<string, string> = {};
    if (_tokenProvider) {
      const token = await _tokenProvider();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(`${API_BASE}/api/ai-ingestion/upload`, {
      method: "POST",
      body: form,
      headers,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
    return res.json();
  },
  listPolicyDrafts: (
    legalEntityId: string,
    statusFilter?: string,
  ): Promise<PolicyExtractionDraftDetail[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (statusFilter) qs.append("status", statusFilter);
    return request(`/api/ai-ingestion/drafts?${qs.toString()}`);
  },
  getPolicyDraft: (id: string): Promise<PolicyExtractionDraftFull> =>
    request(`/api/ai-ingestion/drafts/${encodeURIComponent(id)}`),

  getPolicyDraftDiff: (id: string): Promise<PolicyDraftDiff> =>
    request(`/api/ai-ingestion/drafts/${encodeURIComponent(id)}/diff`),
  approvePolicyItem: (
    draftId: string,
    itemId: string,
    body: { actor?: string } = {},
  ): Promise<PolicyExtractedItem> =>
    request(
      `/api/ai-ingestion/drafts/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/approve`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  rejectPolicyItem: (
    draftId: string,
    itemId: string,
    body: { actor?: string; reason: string },
  ): Promise<PolicyExtractedItem> =>
    request(
      `/api/ai-ingestion/drafts/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/reject`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  editPolicyItem: (
    draftId: string,
    itemId: string,
    payload_json: Record<string, unknown>,
    actor?: string,
  ): Promise<PolicyExtractedItem> =>
    request(
      `/api/ai-ingestion/drafts/${encodeURIComponent(draftId)}/items/${encodeURIComponent(itemId)}/edit`,
      {
        method: "POST",
        body: JSON.stringify({ payload_json, actor }),
      },
    ),
  promotePolicyDraft: (
    draftId: string,
    body: { actor?: string; domains?: string[] } = {},
  ): Promise<PromoteSummary> =>
    request(
      `/api/ai-ingestion/drafts/${encodeURIComponent(draftId)}/promote`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  // ---- M3X.1 v2 F.3 + F.5 — Agent registry + reasoning store ----

  listAgents: (): Promise<AgentsResponse> => request("/api/agents"),

  listAgentReasoningTraces: (params: {
    agent?: string;
    entityId?: string;
    decisionId?: string;
    limit?: number;
  } = {}): Promise<AgentReasoningTracesResponse> => {
    const q = new URLSearchParams();
    if (params.agent) q.set("agent", params.agent);
    if (params.entityId) q.set("entity_id", params.entityId);
    if (params.decisionId) q.set("decision_id", params.decisionId);
    if (params.limit) q.set("limit", String(params.limit));
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request(`/api/agents/reasoning${suffix}`);
  },

  // ---- M3X.WORKSPACE-CLOSURE Session 2 PR 4 — countSource helper ---------
  // Lightweight count helper used by registry countSource callbacks. Returns
  // the .length of an array endpoint response; resolves to 0 on any error.
  countOf: async (path: string): Promise<number> => {
    try {
      const out = await request<unknown>(path);
      if (Array.isArray(out)) return out.length;
      if (out && typeof out === "object") {
        const o = out as Record<string, unknown>;
        if (typeof o.count === "number") return o.count;
        if (Array.isArray(o.items)) return o.items.length;
      }
      return 0;
    } catch {
      return 0;
    }
  },

  // M3X.BENEFITS F.1 benefits plans catalog
  listBenefitsPlans: (
    legalEntityId: string,
    filters: { category?: string; includeInactive?: boolean } = {},
  ): Promise<BenefitsPlan[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.category) qs.append("category", filters.category);
    if (filters.includeInactive) qs.append("include_inactive", "true");
    return request(`/api/benefits-plans?${qs.toString()}`);
  },
  getBenefitsPlan: (id: string): Promise<BenefitsPlanDetail> =>
    request(`/api/benefits-plans/${encodeURIComponent(id)}`),
  createBenefitsPlan: (
    body: Record<string, unknown>,
  ): Promise<BenefitsPlanDetail> =>
    request("/api/benefits-plans", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateBenefitsPlan: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<BenefitsPlanDetail> =>
    request(`/api/benefits-plans/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deactivateBenefitsPlan: (id: string): Promise<{ deactivated: boolean }> =>
    request(`/api/benefits-plans/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  createBenefitTier: (
    planId: string,
    body: Record<string, unknown>,
  ): Promise<BenefitTier> =>
    request(`/api/benefits-plans/${encodeURIComponent(planId)}/tiers`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateBenefitTier: (
    planId: string,
    tierId: string,
    body: Record<string, unknown>,
  ): Promise<BenefitTier> =>
    request(
      `/api/benefits-plans/${encodeURIComponent(planId)}/tiers/${encodeURIComponent(
        tierId,
      )}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  deactivateBenefitTier: (
    planId: string,
    tierId: string,
  ): Promise<{ deactivated: boolean }> =>
    request(
      `/api/benefits-plans/${encodeURIComponent(planId)}/tiers/${encodeURIComponent(
        tierId,
      )}`,
      { method: "DELETE" },
    ),

  // M3X.BENEFITS F.3 CCHI medical-insurance compliance
  listMedicalInsuranceExceptions: (
    legalEntityId: string,
    filters: { status?: string } = {},
  ): Promise<MedicalInsuranceException[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/medical-insurance-exceptions?${qs.toString()}`);
  },
  grantMedicalInsuranceException: (
    body: Record<string, unknown>,
  ): Promise<MedicalInsuranceException> =>
    request("/api/medical-insurance-exceptions", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeMedicalInsuranceException: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<MedicalInsuranceException> =>
    request(
      `/api/medical-insurance-exceptions/${encodeURIComponent(id)}/revoke`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  scanCCHIGaps: (
    legalEntityId: string,
  ): Promise<CCHIGapScanResponse> =>
    request(
      `/api/medical-insurance-exceptions/scan/${encodeURIComponent(
        legalEntityId,
      )}`,
      { method: "POST" },
    ),

  // M3X.BENEFITS F.4-F.6 enrollment workflow
  listBenefitEnrollments: (
    legalEntityId: string,
    filters: { employeeId?: string; status?: string } = {},
  ): Promise<BenefitEnrollment[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/benefit-enrollments?${qs.toString()}`);
  },
  getBenefitEnrollment: (id: string): Promise<BenefitEnrollment> =>
    request(`/api/benefit-enrollments/${encodeURIComponent(id)}`),
  createBenefitEnrollment: (
    body: Record<string, unknown>,
  ): Promise<BenefitEnrollment> =>
    request("/api/benefit-enrollments", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  benefitEnrollmentAction: (
    id: string,
    action: string,
    body?: Record<string, unknown>,
  ): Promise<BenefitEnrollment> =>
    request(
      `/api/benefit-enrollments/${encodeURIComponent(id)}/${action}`,
      {
        method: "POST",
        body: body ? JSON.stringify(body) : "{}",
      },
    ),

  // M3X.BENEFITS F.7 dependent verifications (Sonnet OCR + HR review)
  listDependentVerifications: (
    legalEntityId: string,
    filters: { employeeId?: string; reviewStatus?: string } = {},
  ): Promise<DependentVerification[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.reviewStatus) qs.append("review_status", filters.reviewStatus);
    return request(`/api/dependent-verifications?${qs.toString()}`);
  },
  submitDependentVerification: (
    body: Record<string, unknown>,
  ): Promise<DependentVerification> =>
    request("/api/dependent-verifications", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  reviewDependentVerification: (
    id: string,
    approved: boolean,
    body: Record<string, unknown>,
  ): Promise<DependentVerification> =>
    request(
      `/api/dependent-verifications/${encodeURIComponent(id)}/${
        approved ? "approve" : "reject"
      }`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X.BENEFITS F.9-F.10 beneficiaries register
  listBeneficiaries: (
    legalEntityId: string,
    filters: { employeeId?: string; active?: boolean } = {},
  ): Promise<Beneficiary[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.active === false) qs.append("active", "false");
    return request(`/api/beneficiaries?${qs.toString()}`);
  },
  getBeneficiary: (id: string): Promise<Beneficiary> =>
    request(`/api/beneficiaries/${encodeURIComponent(id)}`),
  createBeneficiary: (body: Record<string, unknown>): Promise<Beneficiary> =>
    request("/api/beneficiaries", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateBeneficiary: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<Beneficiary> =>
    request(`/api/beneficiaries/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deactivateBeneficiary: (id: string): Promise<{ deactivated: boolean }> =>
    request(`/api/beneficiaries/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  checkBeneficiaryAllocation: (
    employeeId: string,
  ): Promise<AllocationCheckResponse> =>
    request(
      `/api/beneficiaries/employee/${encodeURIComponent(
        employeeId,
      )}/allocation-check`,
    ),

  // M3X.BENEFITS F.12 provider invoices
  listProviderInvoices: (
    legalEntityId: string,
    filters: { planId?: string; status?: string } = {},
  ): Promise<ProviderInvoice[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.planId) qs.append("plan_id", filters.planId);
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/provider-invoices?${qs.toString()}`);
  },
  createProviderInvoice: (
    body: Record<string, unknown>,
  ): Promise<ProviderInvoice> =>
    request("/api/provider-invoices", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  runProviderInvoiceOCR: (id: string): Promise<ProviderInvoice> =>
    request(`/api/provider-invoices/${encodeURIComponent(id)}/ocr`, {
      method: "POST",
    }),
  reconcileProviderInvoice: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<ProviderInvoice> =>
    request(
      `/api/provider-invoices/${encodeURIComponent(id)}/reconcile`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  resolveProviderInvoice: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<ProviderInvoice> =>
    request(
      `/api/provider-invoices/${encodeURIComponent(id)}/resolve`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X.BENEFITS F.13-F.14 benefit claims (record-only) + analytics
  listBenefitClaims: (
    legalEntityId: string,
    filters: {
      employeeId?: string;
      planId?: string;
      status?: string;
    } = {},
  ): Promise<BenefitClaim[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.planId) qs.append("plan_id", filters.planId);
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/benefit-claims?${qs.toString()}`);
  },
  createBenefitClaim: (
    body: Record<string, unknown>,
  ): Promise<BenefitClaim> =>
    request("/api/benefit-claims", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateBenefitClaim: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<BenefitClaim> =>
    request(`/api/benefit-claims/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  getBenefitClaimsAnalytics: (
    legalEntityId: string,
  ): Promise<ClaimsAnalytics> =>
    request(
      `/api/benefit-claims/analytics?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}`,
    ),

  // M3X.BENEFITS F.15-F.16 renewal pricing
  listRenewalPricing: (
    legalEntityId: string,
    filters: { status?: string } = {},
  ): Promise<RenewalPricing[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/renewal-pricing?${qs.toString()}`);
  },
  createRenewalPricing: (
    body: Record<string, unknown>,
  ): Promise<RenewalPricing> =>
    request("/api/renewal-pricing", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  approveRenewalPricing: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<RenewalPricing> =>
    request(
      `/api/renewal-pricing/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  rejectRenewalPricing: (
    id: string,
    body: Record<string, unknown>,
  ): Promise<RenewalPricing> =>
    request(
      `/api/renewal-pricing/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // M3X.BENEFITS F.17 voluntary retirement
  listVoluntaryElections: (
    legalEntityId: string,
    filters: { employeeId?: string; status?: string } = {},
  ): Promise<VoluntaryElection[]> => {
    const qs = new URLSearchParams({ legal_entity_id: legalEntityId });
    if (filters.employeeId) qs.append("employee_id", filters.employeeId);
    if (filters.status) qs.append("status", filters.status);
    return request(`/api/voluntary-retirement?${qs.toString()}`);
  },
  createVoluntaryElection: (
    body: Record<string, unknown>,
  ): Promise<VoluntaryElection> =>
    request("/api/voluntary-retirement", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  pauseVoluntaryElection: (id: string): Promise<VoluntaryElection> =>
    request(
      `/api/voluntary-retirement/${encodeURIComponent(id)}/pause`,
      { method: "POST" },
    ),
  cancelVoluntaryElection: (id: string): Promise<VoluntaryElection> =>
    request(
      `/api/voluntary-retirement/${encodeURIComponent(id)}/cancel`,
      { method: "POST" },
    ),

  // M3X.BENEFITS F.22-F.23 reports + dashboards
  benefitsReportCatalog: (): Promise<{ report_ids: string[] }> =>
    request("/api/benefits/reports/_catalog"),
  runBenefitsReport: (
    reportId: string,
    legalEntityId: string,
  ): Promise<Record<string, unknown>> =>
    request(
      `/api/benefits/reports/${encodeURIComponent(
        reportId,
      )}?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  benefitsDashboardHRDirector: (
    legalEntityId: string,
  ): Promise<Record<string, unknown>> =>
    request(
      `/api/benefits/dashboards/hr-director?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}`,
    ),
  benefitsDashboardHRPartner: (
    legalEntityId: string,
  ): Promise<Record<string, unknown>> =>
    request(
      `/api/benefits/dashboards/hr-partner?legal_entity_id=${encodeURIComponent(
        legalEntityId,
      )}`,
    ),

  // Session 3a PR 2 — Probation Watch
  scheduleProbationCheckIn: (
    body: Record<string, unknown>,
  ): Promise<{ id: string; scheduled: boolean }> =>
    request("/api/people/probation-check-in", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Session 3a PR 1 — AI brief endpoint
  getAiBrief: (
    route: string,
    locale: "en" | "ar" = "en",
    context: Record<string, unknown> | undefined = undefined,
  ): Promise<AiBriefResponse> => {
    const qs = new URLSearchParams({ route, locale });
    if (context !== undefined) qs.append("context", JSON.stringify(context));
    return request(`/api/ai-brief?${qs.toString()}`);
  },

  // M3X.GOV.5 hotfix — typed wrappers for the existing GOV read
  // endpoints. Backend already shipped in M3X.GOV; these wrappers let
  // the new sandbox-demo UI fetch + render without ad-hoc fetch calls.
  getGovHealth: (legalEntityId: string): Promise<GovHealthCard[]> =>
    request(
      `/api/gov/health?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  listGovConfigs: (legalEntityId: string): Promise<GovConfigRow[]> =>
    request(
      `/api/gov/config?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  getGovAuditLog: (
    legalEntityId: string,
    portal: string,
    limit: number = 50,
  ): Promise<GovAuditLogRow[]> =>
    request(
      `/api/gov/audit-log?legal_entity_id=${encodeURIComponent(legalEntityId)}&portal=${encodeURIComponent(portal)}&limit=${limit}`,
    ),
  getGovAuditPayload: (
    legalEntityId: string,
    logId: string,
  ): Promise<{ request: unknown; response: unknown }> =>
    request(
      `/api/gov/audit-log/${encodeURIComponent(logId)}/payload?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
    ),
  getGovErrorCatalog: (
    locale: "en" | "ar" = "en",
  ): Promise<
    Record<
      string,
      { code: string; portal: string; category: string; label: string }
    >
  > => request(`/api/gov/error-catalog?locale=${locale}`),
};

// ----- M3X.GOV.5 typed shapes ---------------------------------------------

export interface GovHealthCard {
  portal: "mudad" | "gosi" | "qiwa" | "muqeem";
  mode: "sandbox" | "production";
  health_status: "green" | "amber" | "red" | "unknown";
  last_call_at: string | null;
  circuit_open: boolean;
  cool_off_remaining_s: number;
  accredited?: boolean;
  error_rate_24h?: number;
}

export interface GovConfigRow {
  legal_entity_id: string;
  portal: "mudad" | "gosi" | "qiwa" | "muqeem";
  mode: "sandbox" | "production";
  production_unlocked_at: string | null;
  last_call_at: string | null;
  health_status: "green" | "amber" | "red" | "unknown";
  circuit_open_until: string | null;
}

export interface GovAuditLogRow {
  id: string;
  portal: string;
  endpoint: string;
  direction: "outbound" | "inbound";
  status: string;
  error_category: string | null;
  error_code: string | null;
  duration_ms: number | null;
  correlation_id: string | null;
  created_at: string;
}

export interface AiBriefBullet {
  text: string;
}

export interface AiBriefAction {
  label: string;
  href: string | null;
}

export interface AiBriefResponse {
  route: string;
  agent: "tashghil" | "rawatib" | "mutabaa" | "khidmat";
  locale: "en" | "ar";
  headline: string;
  bullets: AiBriefBullet[];
  action: AiBriefAction | null;
  source: "sonnet" | "deterministic";
}

// ---------------------------------------------------------------------------
// M3X.BENEFITS F.1 — benefits plan catalog types
// ---------------------------------------------------------------------------

export type BenefitsCategory =
  | "medical"
  | "dental"
  | "vision"
  | "life"
  | "disability"
  | "wellness"
  | "retirement";

export interface BenefitTier {
  id: string;
  plan_id: string;
  tier_name: string;
  tier_name_ar: string;
  tier_order: number;
  employer_contribution_pct: string;
  employee_contribution_pct: string;
  coverage_limit_sar_per_year: string | null;
  extras_json: Record<string, unknown> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BenefitsPlan {
  id: string;
  legal_entity_id: string;
  category: BenefitsCategory;
  plan_code: string;
  plan_name: string;
  plan_name_ar: string;
  provider_name: string;
  provider_contract_number: string | null;
  provider_contract_start: string | null;
  provider_contract_end: string | null;
  eligibility_after_hire_months: number;
  eligible_grade_ids: string[];
  saudi_nationals_only: boolean;
  requires_active_contract: boolean;
  dependent_spouse_included: boolean;
  dependent_children_included: boolean;
  dependent_parents_included: boolean;
  max_children_per_employee: number;
  children_max_age: number;
  children_max_age_in_education: number;
  max_spouses: number;
  annual_window_open_month: number;
  annual_window_open_day: number;
  annual_window_close_month: number;
  annual_window_close_day: number;
  new_hire_window_days: number;
  qualifying_life_events: string[];
  auto_renew: boolean;
  renewal_notification_days_before: number;
  cchi_compliant: boolean;
  applicable_jurisdictions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BenefitsPlanDetail extends BenefitsPlan {
  tiers: BenefitTier[];
}

// M3X.BENEFITS F.3 — CCHI medical-insurance compliance types
export interface MedicalInsuranceException {
  id: string;
  legal_entity_id: string;
  employee_id: string;
  reason_code: string;
  reason_detail: string;
  effective_from: string;
  effective_until: string;
  status: "active" | "expired" | "revoked";
  granted_by_clerk_user_id: string;
  granted_at: string;
  revoked_at: string | null;
  revoked_by_clerk_user_id: string | null;
  revoke_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface CCHIGapItem {
  employee_id: string;
  hire_date: string;
  days_since_hire: number;
  employee_code: string | null;
  employee_name_en: string | null;
}

export interface CCHIGapScanResponse {
  legal_entity_id: string;
  today: string;
  total_gaps: number;
  items: CCHIGapItem[];
}

// M3X.BENEFITS F.4-F.6 — enrollment workflow types
export type EnrollmentTrigger =
  | "annual_window"
  | "new_hire"
  | "qualifying_life_event";

export type EnrollmentStatus =
  | "draft"
  | "eligibility_checked"
  | "submitted"
  | "pending_review"
  | "approved"
  | "rejected"
  | "effective"
  | "closed"
  | "cancelled";

// M3X.BENEFITS F.9-F.10 — beneficiaries types
export interface Beneficiary {
  id: string;
  legal_entity_id: string;
  employee_id: string;
  full_name: string;
  full_name_ar: string;
  relationship: string;
  national_id: string | null;
  iqama: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  bank_iban: string | null;
  bank_iban_validated: boolean;
  primary: boolean;
  allocation_pct: string;
  beneficiary_for: string[];
  is_active: boolean;
  added_date: string;
  last_updated_date: string;
  created_at: string;
  updated_at: string;
}

export interface AllocationCheckResponse {
  employee_id: string;
  sums_by_kind: Record<string, string>;
  balanced: boolean;
  issues: string[];
}

// M3X.BENEFITS F.15-F.17 — renewal + voluntary retirement
export interface RenewalPricing {
  id: string;
  legal_entity_id: string;
  plan_id: string;
  renewal_year: number;
  effective_from: string;
  old_total_monthly_premium: string;
  new_total_monthly_premium: string;
  variance_pct: string;
  variance_threshold_pct: string;
  status: string;
  proposed_by_clerk_user_id: string | null;
  approved_by_clerk_user_id: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface VoluntaryElection {
  id: string;
  legal_entity_id: string;
  employee_id: string;
  plan_id: string;
  election_pct: string;
  employer_match_pct_of_election: string;
  employer_match_monthly_cap: string | null;
  fund_manager: string | null;
  effective_from: string;
  effective_until: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

// M3X.BENEFITS F.13-F.14 — benefit claims (record-only) + analytics
export interface BenefitClaim {
  id: string;
  legal_entity_id: string;
  plan_id: string;
  employee_id: string;
  dependent_id: string | null;
  provider_claim_id: string | null;
  claim_type: string;
  claim_status: string;
  gross_amount: string;
  employer_paid: string;
  employee_paid: string;
  currency: string;
  submitted_date: string;
  settled_date: string | null;
  provider_notes: string | null;
  document_urls: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClaimsAnalytics {
  legal_entity_id: string;
  period_from: string;
  period_to: string;
  total_claims: number;
  total_gross: string;
  average_cycle_days: number | null;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_provider: Array<{
    provider_name: string;
    claim_count: number;
    total_gross: string;
    total_employer_paid: string;
  }>;
  high_claim_watchlist: Array<{
    employee_id: string;
    employee_code: string | null;
    employee_name_en: string | null;
    claim_count: number;
    total_gross: string;
  }>;
  plan_utilization: Array<{
    plan_id: string;
    plan_code: string;
    plan_name: string;
    claim_count: number;
    enrolled_employees: number;
    utilization_pct: number;
  }>;
}

// M3X.BENEFITS F.12 — provider invoices
export interface ProviderInvoice {
  id: string;
  legal_entity_id: string;
  plan_id: string;
  provider_invoice_number: string | null;
  billing_period_year: number;
  billing_period_month: number;
  invoice_date: string | null;
  expected_total: string;
  actual_total: string | null;
  tolerance: string;
  document_url: string | null;
  ocr_status: string;
  ocr_payload_json: Record<string, unknown> | null;
  ocr_error: string | null;
  status: string;
  resolution_notes: string | null;
  resolved_by_clerk_user_id: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DependentVerification {
  id: string;
  legal_entity_id: string;
  employee_id: string;
  employee_dependent_id: string | null;
  document_kind_declared: string;
  document_url: string;
  ocr_status: string;
  ocr_result_json: Record<string, unknown> | null;
  ocr_confidence: number | null;
  ocr_error: string | null;
  extracted_document_kind: string | null;
  extracted_full_name: string | null;
  extracted_full_name_ar: string | null;
  extracted_date_of_event: string | null;
  extracted_issuance_jurisdiction: string | null;
  review_status: string;
  reviewed_by_clerk_user_id: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BenefitEnrollment {
  id: string;
  legal_entity_id: string;
  plan_id: string;
  tier_id: string;
  employee_id: string;
  trigger_kind: EnrollmentTrigger;
  qle_kind: string | null;
  qle_event_date: string | null;
  selected_dependent_ids: string[];
  effective_from: string;
  effective_until: string | null;
  employee_contribution_per_cycle: string;
  employer_contribution_per_cycle: string;
  status: EnrollmentStatus;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  effective_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  approved_by_clerk_user_id: string | null;
  rejected_by_clerk_user_id: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// M3X.1 v2 F.3 + F.5 — Agent registry types
// ---------------------------------------------------------------------------

export interface AgentSummary {
  agent_id: string;
  agent_class: string;
  goal_statement: string;
  arabic_display_name: string;
  domain: string[];
  handoff_vocabulary: string[];
  authorization_scope: Record<string, string[]>;
  last_heartbeat_at: string | null;
  health: string;
  registered_at: string;
}

export interface AgentsResponse {
  agents: AgentSummary[];
  count: number;
}

export interface AgentReasoningTrace {
  id: string;
  tenant_id: string;
  agent_id: string;
  decision_id: string;
  entity_type: string | null;
  entity_id: string | null;
  goal_pursued: string;
  plan_steps: Record<string, unknown>[];
  tool_calls: Record<string, unknown>[];
  outcomes: Record<string, unknown>;
  escalation_rationale: string | null;
  confidence: number | null;
  mode_at_decision: string | null;
  policy_consulted: string[];
  prev_hash: string | null;
  crypto_hash: string;
  created_at: string;
}

export interface AgentReasoningTracesResponse {
  traces: AgentReasoningTrace[];
  count: number;
}

// Multipart upload helper — fetch() directly since request() pins
// Content-Type: application/json which breaks FormData boundary autodetection.
async function _uploadFile<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {};
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const tenant = tenantContext();
  if (tenant.slug) headers["X-Tenant-Slug"] = tenant.slug;

  const fd = new FormData();
  fd.append("file", file);

  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: fd,
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body?.detail) {
        detail = typeof body.detail === "string"
          ? body.detail
          : JSON.stringify(body.detail);
      }
    } catch {
      // fall through
    }
    throw new ApiError(r.status, detail);
  }
  return (await r.json()) as T;
}

// Multipart upload with a caller-built FormData (file + extra fields).
// Same Content-Type handling as _uploadFile — let the browser set the
// multipart boundary.
async function _uploadForm<T>(path: string, fd: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const tenant = tenantContext();
  if (tenant.slug) headers["X-Tenant-Slug"] = tenant.slug;

  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: fd,
  });
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const body = await r.json();
      if (body?.detail) {
        detail =
          typeof body.detail === "string"
            ? body.detail
            : JSON.stringify(body.detail);
      }
    } catch {
      // fall through
    }
    throw new ApiError(r.status, detail);
  }
  return (await r.json()) as T;
}

/**
 * XHR-based upload that surfaces bytes-progress events. `fetch()`
 * doesn't expose upload bytes natively (the WHATWG fetch's body
 * is opaque to the browser progress hooks), so we drop to XHR
 * for the one case where we genuinely need the % indicator —
 * the M29 import wizard's UploadPhase. Same auth + tenant
 * headers as _uploadForm.
 */
export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–1; null when the server didn't send Content-Length and
   * total can't be computed (rare for FormData uploads). */
  fraction: number | null;
}

export async function _uploadFormWithProgress<T>(
  path: string,
  fd: FormData,
  onProgress: (p: UploadProgress) => void,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const tenant = tenantContext();
  if (tenant.slug) headers["X-Tenant-Slug"] = tenant.slug;

  return await new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}${path}`);
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.addEventListener("progress", (e) => {
      onProgress({
        loaded: e.loaded,
        total: e.total,
        fraction:
          e.lengthComputable && e.total > 0 ? e.loaded / e.total : null,
      });
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch (exc) {
          reject(
            new ApiError(xhr.status, "Server returned non-JSON response"),
          );
        }
      } else {
        let detail = xhr.statusText;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.detail) {
            detail =
              typeof body.detail === "string"
                ? body.detail
                : JSON.stringify(body.detail);
          }
        } catch {
          // fall through
        }
        reject(new ApiError(xhr.status, detail));
      }
    });
    xhr.addEventListener("error", () =>
      reject(new ApiError(0, "Network error during upload")),
    );
    xhr.addEventListener("abort", () =>
      reject(new ApiError(0, "Upload cancelled")),
    );
    xhr.send(fd);
  });
}

// ---------- M7.4 wizard types -------------------------------------------

export type DateDisplayPreference =
  | "hijri_primary"
  | "gregorian_primary"
  | "both";

export type OnboardingStatus = "draft" | "in_progress" | "live";

export interface OnboardingGroupRequest {
  name_en: string;
  name_ar: string;
  billing_contact_email: string;
  primary_hr_director_clerk_user_id?: string | null;
  timezone?: string;
  default_currency?: string;
  date_display_preference?: DateDisplayPreference;
}

export interface OnboardingGroupResponse {
  id: string;
  name_en: string;
  name_ar: string;
  billing_contact_email: string;
  primary_hr_director_clerk_user_id: string | null;
  timezone: string;
  default_currency: string;
  date_display_preference: DateDisplayPreference;
  onboarding_status: OnboardingStatus;
}

/**
 * M7 pre-tenant Setup wizard state. Renamed to OnboardingWizardState
 * in M27 PR 1 so the canonical OnboardingState type (from
 * ./onboarding) belongs to the 9-step post-signup journey.
 */
export interface OnboardingWizardState {
  group: OnboardingGroupResponse | null;
  legal_entities_count: number;
  onboarding_status: OnboardingStatus | null;
  next_step:
    | "group"
    | "entities"
    | "org-structure"
    | "employees"
    | "shadow-payroll"
    | "completed";
}

// ---------- M7.5 legal-entity types -------------------------------------

export interface LegalEntityInput {
  id?: string | null;
  name_en: string;
  name_ar: string;
  cr_number: string;
  gosi_establishment_id?: string | null;
  qiwa_unified_number?: string | null;
  zatca_tax_id?: string | null;
  registered_address?: string | null;
  registered_city?: string | null;
  region?: string | null;
  default_bank_account_name?: string | null;
}

export interface LegalEntityResponse extends LegalEntityInput {
  id: string;
  customer_group_id: string;
}

export interface OnboardingEntitiesRequest {
  entities: LegalEntityInput[];
}

export interface OnboardingEntitiesResponse {
  entities: LegalEntityResponse[];
}

// ---------- M7.6 org-structure types ------------------------------------

export interface BranchInput {
  id?: string | null;
  name_en: string;
  name_ar: string;
  city?: string | null;
}

export interface DepartmentInput {
  id?: string | null;
  parent_department_id?: string | null;
  name_en: string;
  name_ar: string;
}

export interface JobTitleInput {
  id?: string | null;
  title_en: string;
  title_ar: string;
  grade_level?: string | null;
}

export interface OrgStructureRequest {
  legal_entity_id: string;
  branches: BranchInput[];
  departments: DepartmentInput[];
  job_titles: JobTitleInput[];
}

export interface OrgStructureResponse {
  legal_entity_id: string;
  branches: { id: string; name_en: string; name_ar: string; city: string | null }[];
  departments: {
    id: string;
    parent_department_id: string | null;
    name_en: string;
    name_ar: string;
  }[];
  job_titles: {
    id: string;
    title_en: string;
    title_ar: string;
    grade_level: string | null;
  }[];
}

export interface SaudiCity {
  en: string;
  ar: string;
  region: string;
}

export interface SaudiCitiesResponse {
  cities: SaudiCity[];
}

// ---------- M7.7 employee-import types ----------------------------------

export interface EmployeeImportRowError {
  row: number;
  field: string;
  message: string;
}

export interface EmployeeImportPreview {
  total_rows_seen: number;
  valid_row_count: number;
  error_count: number;
  first_errors: EmployeeImportRowError[];
  truncated: boolean;
}

export interface EmployeeImportCommit {
  written: number;
}

// ---------- M7.8 shadow-payroll types -----------------------------------

export interface ShadowPayrollLine {
  name: string;
  expected: string;
  computed: string;
  delta: string;
  matches: boolean;
}

export interface ShadowPayrollEmployee {
  employee_code: string;
  employee_name_en: string | null;
  line_items: ShadowPayrollLine[];
  overall_matches: boolean;
}

export interface ShadowPayrollResponse {
  period_start: string;
  period_end: string;
  employees: ShadowPayrollEmployee[];
  unmatched_codes_in_csv: string[];
  unmatched_codes_in_db: string[];
  overall_matches: boolean;
}

// ---------- M7.9 completion type ----------------------------------------

export interface OnboardingCompletionResponse {
  customer_group_id: string;
  onboarding_status: OnboardingStatus;
  message: string;
}

// ---------- M9.1 signup types -------------------------------------------

export interface SignupSlugCheck {
  slug: string;
  available: boolean;
  reason: string | null;
}

export interface SignupRequest {
  slug: string;
  company_name_en: string;
  company_name_ar: string;
}

export interface SignupResponse {
  tenant_id: string;
  slug: string;
  clerk_org_id: string;
  url: string;
  message: string;
}

// M9 async signup — POST /api/signup now returns 202 with a polling URL.
export interface SignupAcceptedResponse {
  tenant_id: string;
  slug: string;
  status: "pending";
  status_url: string;
  poll_interval_seconds: number;
  message: string;
}

// GET /api/signup/status/{tenant_id} — frontend polls until ready or failed.
export interface SignupStatusResponse {
  tenant_id: string;
  slug: string;
  status: "pending" | "ready" | "failed";
  error: string | null;
  redirect_url: string | null;
  // Latest progress message from the background task — surfaced to
  // the user so they see what's actually happening instead of a
  // static placeholder during the 60-120s wait.
  progress: string | null;
  // The Clerk Organization id the user was just added to (populated
  // only when status == "ready"). The polling frontend uses this to
  // call clerk.setActive({organization}) BEFORE redirecting to the
  // tenant subdomain — without that, the user lands on the subdomain
  // with no active org and the JWT carries no org_id claim, so the
  // tenant resolver 401s every API call.
  clerk_org_id: string | null;
}

// ---------- M8.5+M8.6 Integrations types --------------------------------

export interface Connection {
  integration_type: string;
  label: string;
  category: string;
  configured: boolean;
  required_fields: string[];
  optional_fields: string[];
  updated_at: string | null;
  key_version: number | null;
}

export interface ConnectionsListResponse {
  legal_entity_id: string;
  connections: Connection[];
}

export interface ConnectionUpsert {
  legal_entity_id: string;
  integration_type: string;
  payload: Record<string, string>;
}

export interface ConnectionPing {
  integration_type: string;
  ok: boolean;
  message: string;
}

// ---------- M8.9 + M8.10 AI Config types --------------------------------

export interface AIConfig {
  id: string;
  legal_entity_id: string;
  agent_name: string;
  enabled: boolean;
  auto_resolve_thresholds: Record<string, unknown>;
  channels_enabled: Record<string, unknown>;
  persona: Record<string, unknown>;
  boundaries: Record<string, unknown>;
  working_hours: Record<string, unknown>;
  updated_at: string;
}

export interface AIConfigsResponse {
  legal_entity_id: string;
  configs: AIConfig[];
}

export interface AIConfigPatch {
  enabled?: boolean;
  auto_resolve_thresholds?: Record<string, unknown>;
  channels_enabled?: Record<string, unknown>;
  persona?: Record<string, unknown>;
  boundaries?: Record<string, unknown>;
  working_hours?: Record<string, unknown>;
}

export interface AIConfigHistoryEntry {
  id: string;
  snapshot: Record<string, unknown>;
  changed_by_clerk_user_id: string | null;
  changed_at: string;
}

export interface AIConfigHistoryResponse {
  agent_name: string;
  entries: AIConfigHistoryEntry[];
}

export interface KnowledgeDoc {
  id: string;
  filename: string;
  scope: string;
  size_bytes: number;
  uploaded_at: string;
}

export interface KnowledgeListResponse {
  documents: KnowledgeDoc[];
}

// ---------- M11.3 employee master file types ----------------------------

export interface EmployeeDependentDTO {
  id: string;
  name: string;
  name_ar: string | null;
  date_of_birth: string;
  relationship: "spouse" | "child" | "parent" | "sibling" | "other";
  residence_status: "ksa_resident" | "abroad";
  schooling_eligible: boolean;
  national_id_or_iqama: string | null;
}

export interface EmergencyContactDTO {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  priority: number;
}

export interface PassportDTO {
  id: string;
  passport_number: string;
  issuing_country: string;
  issued_date: string;
  expiry_date: string;
  is_primary: boolean;
}

export interface BankAccountDTO {
  id: string;
  bank_name: string;
  iban: string;
  account_holder_name: string;
  distribution_percent: string;
  is_active: boolean;
}

export interface CurrentPositionDTO {
  position_id: string;
  position_number_display: string;
  title_en: string;
  title_ar: string;
  department_name_en: string | null;
  assignment_start: string;
  reason_code: string | null;
}

export interface EmployeeEventDTO {
  id: string;
  event_type: string;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  actor_clerk_user_id: string | null;
  reason_code: string | null;
  notes: string | null;
  occurred_at: string;
}

export interface EmployeeMasterFile {
  id: string;
  customer_id: string;
  legal_entity_id: string | null;
  employee_code: string;

  first_name_en: string;
  first_name_ar: string;
  middle_name_en: string | null;
  middle_name_ar: string | null;
  last_name_en: string;
  last_name_ar: string;

  date_of_birth: string;
  gender: string;
  nationality: string;
  marital_status: string | null;
  religion: string | null;
  mother_tongue: string | null;
  photo_url: string | null;

  is_saudi_national: boolean;
  national_id_or_iqama: string;
  mol_number: string | null;
  driver_license_number: string | null;
  national_id_issued: string | null;
  national_id_expiry: string | null;
  iqama_issued: string | null;
  iqama_expiry: string | null;
  driver_license_issued: string | null;
  driver_license_expiry: string | null;
  passports: PassportDTO[];

  phone_number: string | null;
  alternate_phone: string | null;
  work_email: string | null;
  personal_email: string | null;
  current_address_line: string | null;
  current_address_city: string | null;
  current_address_region: string | null;
  permanent_address_line: string | null;
  permanent_address_city: string | null;
  permanent_address_region: string | null;

  dependents: EmployeeDependentDTO[];
  emergency_contacts: EmergencyContactDTO[];
  bank_accounts: BankAccountDTO[];

  hire_date: string;
  original_hire_date: string | null;
  employment_status: string;
  role_category: string;
  first_gosi_contribution_date: string | null;
  work_permit_expiry: string | null;
  saudization_classification: string | null;
  terminated_at: string | null;
  termination_reason: string | null;
  // M21.9 — the open M18 termination workflow, if one exists.
  active_termination_id: string | null;
  active_termination_state: string | null;

  current_position: CurrentPositionDTO | null;
  recent_events: EmployeeEventDTO[];
}

// ---------- M12.7 contract detail types ---------------------------------

export interface ContractEmployeeSummary {
  id: string;
  employee_code: string;
  first_name_en: string;
  last_name_en: string;
}

export interface ContractPositionSummary {
  id: string;
  position_number_display: string;
  title_en: string;
}

export interface ContractEventEntry {
  id: string;
  event_type: string;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  effective_date: string | null;
  actor_clerk_user_id: string | null;
  reason_code: string | null;
  notes: string | null;
  occurred_at: string;
}

export type {
  OnboardingState,
  StepSlug,
  StepStatus,
  StepState,
} from "./onboarding";

export interface DashboardSummary {
  key: "headcount" | "turnover" | "compensation" | "compliance";
  label: string;
  quick_summary: string;
  last_refreshed: string | null;
  link_to: string;
}

export interface BillingUsage {
  period_start: string;
  period_end: string;
  active_employees: number;
  active_legal_entities: number;
  ai_calls_this_month: number;
  ai_cost_usd_this_month: string;
  ai_cache_hit_rate: number;
  concierge_messages_this_month: number;
  storage_used_mb: number;
}

export interface LegalEntityListItem {
  id: string;
  name_en: string;
  name_ar: string;
  cr_number: string;
  gosi_establishment_id: string | null;
  qiwa_unified_number: string | null;
  zatca_tax_id: string | null;
  registered_city: string | null;
  region: string | null;
  archived_at: string | null;
  employee_count: number;
  active_contract_count: number;
  created_at: string;
}

export interface LegalEntityEvent {
  id: string;
  event_type: string;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  actor_clerk_user_id: string | null;
  notes: string | null;
  occurred_at: string;
}

export interface LegalEntityStats {
  employee_count: number;
  active_contract_count: number;
  saudi_employee_count: number;
  saudization_percent: number;
  saudization_band: string;
}

export interface LegalEntityDetail {
  id: string;
  customer_group_id: string;
  name_en: string;
  name_ar: string;
  cr_number: string;
  zatca_tax_id: string | null;
  registered_address: string | null;
  registered_city: string | null;
  region: string | null;
  hr_contact_email: string | null;
  gosi_establishment_id: string | null;
  qiwa_unified_number: string | null;
  default_bank_account_name: string | null;
  default_bank_iban: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  stats: LegalEntityStats;
  recent_events: LegalEntityEvent[];
}

export interface LegalEntityCreateBody {
  name_en: string;
  name_ar: string;
  cr_number: string;
  gosi_establishment_id?: string | null;
  qiwa_unified_number?: string | null;
  zatca_tax_id?: string | null;
  registered_address?: string | null;
  registered_city?: string | null;
  region?: string | null;
  hr_contact_email?: string | null;
  default_bank_account_name?: string | null;
  default_bank_iban?: string | null;
}

export interface TenantStats {
  active_users_count: number;
  total_employees: number;
  total_legal_entities: number;
  total_active_contracts: number;
}

export interface TenantSettings {
  customer_group_id: string;
  customer_group_name_en: string;
  customer_group_name_ar: string;
  slug: string;
  region: string;
  created_at: string;
  billing_contact_email: string;
  timezone: string;
  default_currency: string;
  date_display_preference: "hijri_primary" | "gregorian_primary" | "both";
  default_language: "en" | "ar";
  stats: TenantStats;
}

export interface TenantSettingsEditable {
  billing_contact_email: string;
  timezone: string;
  default_currency: string;
  date_display_preference: "hijri_primary" | "gregorian_primary" | "both";
  default_language: "en" | "ar";
}

// ---- Users & Roles (Step 2 of in-app user management) ----------------

export type TenantRole =
  | "tenant_admin"
  | "hr_director"
  | "hr_partner"
  | "manager"
  | "employee";

export const TENANT_ROLES: TenantRole[] = [
  "tenant_admin",
  "hr_director",
  "hr_partner",
  "manager",
  "employee",
];

export const ROLE_LABEL_EN: Record<TenantRole, string> = {
  tenant_admin: "Tenant Admin",
  hr_director: "HR Director",
  hr_partner: "HR Partner",
  manager: "Manager",
  employee: "Employee",
};

export interface TenantMember {
  user_id: string;
  email: string | null;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  image_url: string | null;
  role: TenantRole;
  status: "active" | "suspended";
  joined_at_ms: number | null;
  last_active_at_ms: number | null;
}

export interface TenantInvitation {
  invitation_id: string;
  email: string;
  role: TenantRole;
  status: string;
  inviter_user_id: string | null;
  created_at_ms: number | null;
}

export interface TenantMemberDetail extends TenantMember {
  other_org_memberships:
    | Array<{
        organization_id: string;
        organization_name: string | null;
        organization_slug: string | null;
        role: TenantRole;
      }>
    | null;
  recent_actions:
    | Array<{
        action: string;
        target_email: string | null;
        occurred_at: string | null;
        before: Record<string, unknown> | null;
        after: Record<string, unknown> | null;
      }>
    | null;
}

export interface HolidayItem {
  id: string;
  legal_entity_id: string | null;
  name_en: string;
  name_ar: string;
  observed_date: string;
  recurrence: "annual" | "one_time";
  source: "statutory" | "company";
  note: string | null;
}

export interface ContractsListItem {
  id: string;
  contract_number: number | null;
  employee: {
    id: string;
    employee_code: string;
    first_name_en: string;
    last_name_en: string;
    first_name_ar: string | null;
    last_name_ar: string | null;
  };
  position: {
    id: string;
    position_number_display: string;
    title_en: string;
  } | null;
  legal_entity_id: string | null;
  contract_type: string;
  contract_status: string | null;
  employment_type: string | null;
  start_date: string;
  end_date: string | null;
  probation_end_date: string | null;
  basic_salary_amount: string;
  basic_salary_currency: string;
}

export interface ContractDetail {
  id: string;
  contract_number: number | null;
  employee: ContractEmployeeSummary;
  position: ContractPositionSummary | null;
  legal_entity_id: string | null;
  contract_type: string;
  contract_period: string | null;
  contract_status: string | null;
  employment_type: string | null;
  start_date: string;
  end_date: string | null;
  note: string | null;

  pay_frequency: string;
  basic_salary_amount: string;
  basic_salary_currency: string;
  exclude_from_wps: boolean;
  qiwa_authenticated: boolean;
  qiwa_authentication_date: string | null;
  work_permit_profession: string | null;

  probation_period_days: number;
  probation_end_date: string | null;
  stop_leave_accrual: boolean;
  stop_ticket_accrual: boolean;
  stop_si_accrual: boolean;
  stop_eos_accrual: boolean;
  stop_medical_insurance_accrual: boolean;

  housing_allowance_amount: string;
  housing_allowance_currency: string;
  transportation_allowance_amount: string;
  transportation_allowance_currency: string;
  food_allowance_amount: string;
  food_allowance_currency: string;
  other_fixed_allowances_amount: string;
  other_fixed_allowances_currency: string;
  housing_by_company: boolean;
  transportation_by_company: boolean;

  comp_overrides: Record<string, unknown>;
  resolution_paths: Record<string, ResolutionLayerEntry[]>;

  recent_events: ContractEventEntry[];
}

// ---------- M15 leave type types -----------------------------------------

export interface LeaveTypeDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  mask_en: string | null;
  mask_ar: string | null;
  leave_color: string | null;
  description: string | null;

  category: string;
  ticket_category: string;
  nationality_filter: string[] | null;
  religion_filter: string | null;
  gender_filter: string | null;
  marital_status_filter: string | null;
  eligibility_time_basis: string;
  leave_eligible_from: string | null;
  eligible_after_months: number;
  ticket_eligible_from: string | null;
  ticket_eligible_after_months: number;

  balance_calculation_type: string;
  number_of_days: string | null;
  days_per_transaction: string | null;
  every_no_of_days: number | null;
  remove_holidays_from_transaction: boolean;
  remove_weekends_from_transaction: boolean;
  days_will_be_deducted_from_service_period: boolean;
  calculate_weekend_based_on_leave_calc: boolean;
  allow_negative_balance: boolean;
  maximum_negative_days: string | null;
  balanced: boolean;
  ticket_balanced: boolean;
  leave_carry_over_by_service_period: boolean;
  carry_over_days: string | null;
  convert_to_absence_when_no_balance: boolean;
  get_balance_from_this_leave_when_no_balance: string | null;
  activate_days_prior_leave: boolean;
  active_days_work_from_home: boolean;
  weekend_eligibility: string;
  weekend_number_of_days: number;
  encashable_on_eos: boolean;

  requires_attachment_before_request: boolean;
  pay_leave_with_salary: boolean;
  payment_ranges_per: string;
  include_in_pay_cycle: boolean;
  auto_return: boolean;
  allow_users_with_expired_contract_to_request: boolean;
  fill_to_date_based_on_balance: boolean;
  hide_leave_balance: boolean;
  show_leave_balance_according_to_date: boolean;
  carry_over_after_cutoff_date: boolean;
  activate_pay_in_advance: boolean;
  min_days_to_get_salary_in_advance: number | null;
  auto_return_based_on_leave_days: boolean;
  auto_return_before_days: number | null;
  vacation_alternative_mandatory: boolean;

  rule_id_for_amount_calculation: string | null;
  advance_validation: Array<Record<string, unknown>>;

  is_active: boolean;
  created_at: string;
  updated_at: string;

  // M3X — Section A-G PR1 knobs surfaced on detail page
  description_en?: string | null;
  description_ar?: string | null;
  nationality_excluding?: string[] | null;
  relationship_active?: boolean | null;
  office_location_restriction?: string[] | null;
  maternity_infant_special_need?: boolean | null;
  deduct_from_service_period_in_eos?: number | null;
  maternity_leave_special_accrual?: boolean | null;
  leave_carry_over_for_selected_year?: boolean | null;
  allow_exceeding_balance_to_unpaid?: boolean | null;
  carry_over_with_pay_transaction?: boolean | null;
  hide_sss_disable_alternative_employee?: boolean | null;
  hide_sss_hide_leave_planned?: boolean | null;
  hide_sss_hide_notes?: boolean | null;
  to_be_shown_on_mss?: boolean | null;
  leave_amount_number_of_days_to_pay?: number | null;
  pay_rate_tier_formula_ref?: string | null;
}

// ---------- M14 allowance types ------------------------------------------

export interface AllowanceDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  allowance_type: "allowance" | "deduction" | "cost";
  category:
    | "housing"
    | "transport"
    | "communication"
    | "iqama"
    | "medical"
    | "performance"
    | "other";
  amount_calculation: "fixed" | "percent" | "per_day" | "formula";
  fixed_amount: string | null;
  percent: string | null;
  per_day_amount: string | null;
  formula_ref: string | null;
  included_in_tax: boolean;
  included_in_gosi: boolean;
  included_in_eos_base: boolean;
  included_in_sub_cycle: boolean;
  minimum_amount: string | null;
  maximum_amount: string | null;
  fixed_monthly_amount: string | null;
  currency: string;
  si_payments: string | null;
  stb_provision_calculation_formula: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AllowanceAssignment {
  id: string;
  allowance_id: string;
  amount_override: string | null;
}

// ---------- M13 grade types ----------------------------------------------

export interface GradeStepDTO {
  id: string;
  step_number: number;
  salary_from: string;
  salary_to: string;
  currency: string;
  salary_scale_category: string | null;
  indicator_only: boolean;
}

// M3X.COMP F.17 — annual merit cycle.
export interface CompCycleDTO {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  effective_date: string;
  budget_envelope_pct: string;
  cost_of_living_pct: string;
  pre_cycle_annual_base: string;
  modeled_annual_base: string;
  workflow_state: string;
  notes: string | null;
  sonnet_narrative_en: string | null;
  sonnet_narrative_ar: string | null;
  started_at: string;
  approved_at: string | null;
  posted_at: string | null;
  cancelled_at: string | null;
}

export interface CompCycleEntryDTO {
  id: string;
  comp_cycle_id: string;
  employee_id: string;
  pre_cycle_basic_salary: string;
  performance_multiplier: string;
  proposed_basic_salary: string;
  delta_pct: string | null;
  below_band: boolean;
  above_cap: boolean;
  comp_change_id: string | null;
}

// M3X.COMP F.C — per-employee comp history aggregator.
export interface CompHistoryEntry {
  entry_kind: string;
  entry_id: string;
  effective_date: string;
  headline_en: string;
  headline_ar: string;
  previous_value: string | null;
  new_value: string | null;
  delta_pct: string | null;
  workflow_state: string | null;
  change_type: string | null;
}

export interface CompAnalytics {
  current_basic_salary: string | null;
  initial_basic_salary: string | null;
  cumulative_growth_pct: string | null;
  yoy_cagr_pct: string | null;
  tenure_years: string | null;
  current_compa_ratio: string | null;
  last_change_date: string | null;
  days_since_last_change: number | null;
}

export interface EmployeeCompHistory {
  employee_id: string;
  timeline: CompHistoryEntry[];
  analytics: CompAnalytics;
}

// M3X.COMP F.5 + F.6 — comp_changes workflow record.
export interface CompChangeDTO {
  id: string;
  change_number: number | null;
  employee_id: string;
  legal_entity_id: string;
  comp_cycle_id: string | null;
  change_type: string;
  change_reason: string | null;
  change_reason_classification: string | null;
  effective_date: string;
  previous_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  delta: Record<string, unknown>;
  previous_basic_salary: string | null;
  new_basic_salary: string | null;
  delta_pct: string | null;
  article_60_required: boolean;
  article_60_consent_signed: boolean;
  article_60_consent_date: string | null;
  article_60_consent_pdf_url: string | null;
  workflow_state: string;
  current_approver_employee_id: string | null;
  approval_step_index: number;
  submitted_at: string | null;
  approved_at: string | null;
  effective_at: string | null;
  closed_at: string | null;
  rejected_at: string | null;
  cancelled_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowEventDTO {
  id: string;
  workflow_type: string;
  workflow_id: string;
  from_state: string | null;
  to_state: string;
  transition: string;
  actor_clerk_user_id: string | null;
  reason: string | null;
  payload_diff: Record<string, unknown>;
  occurred_at: string;
}

// M3X.COMP F.4 — tiered/capped commission rules per LE.
export interface CommissionStructureDTO {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  type: string;
  rate_rules: Array<{ threshold: string; rate: string }>;
  cap_per_month: string | null;
  cap_per_year: string | null;
  currency: string;
  payment_cadence: string;
  applicable_jurisdictions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// M3X.COMP F.1 — per-LE per-grade per-step comp band catalog.
// Equity columns are OUT of v1 per Amer's scope lock.
export interface CompBandDTO {
  id: string;
  legal_entity_id: string;
  grade_id: string;
  grade_step: number;
  min_salary: string;
  mid_salary: string;
  max_salary: string;
  currency: string;
  bonus_target_pct_of_base: string;
  bonus_cap_pct_of_base: string;
  bonus_cycle_frequency: string | null;
  commission_eligible: boolean;
  commission_structure_id: string | null;
  market_p25_salary: string | null;
  market_p50_salary: string | null;
  market_p75_salary: string | null;
  market_p90_salary: string | null;
  market_data_source: string | null;
  market_data_as_of: string | null;
  applicable_jurisdictions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GradeDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  parent_grade_id: string | null;
  description: string | null;
  pay_group: string | null;
  functional_groups: string | null;
  qualification_required: string | null;

  insurance_coverage_level: string | null;
  members_covered_count: number | null;
  insurance_level_id: string | null;
  insurance_category: string | null;
  business_travel_id: string | null;
  air_class: string | null;
  hotel_rate: string | null;
  housing_by_company: boolean | null;
  transportation_provided: boolean | null;
  schooling_amount: string | null;
  school_amount_eligibility: string | null;
  allow_exceeding_schooling: boolean | null;
  eos_calculation_method: string | null;
  competencies_percentage: number | null;
  objectives_percentage: number | null;
  annual_leave_days_default: number | null;
  ticket_count_per_year: number | null;
  ticket_class: string | null;
  ticket_entitlement_period: string | null;

  created_at: string;
  updated_at: string;
  steps: GradeStepDTO[];
}

export interface GradeResolvedField {
  value: unknown;
  source_grade_id: string | null;
}

export interface GradeResolved {
  grade_id: string;
  chain: string[];
  fields: Record<string, GradeResolvedField>;
}

// ---------- M12.6 probation tracking types ------------------------------

export interface ProbationEndingSoonItem {
  contract_id: string;
  contract_number: number | null;
  employee_id: string;
  employee_code: string;
  employee_first_name_en: string;
  employee_last_name_en: string;
  start_date: string;
  probation_end_date: string;
  days_remaining: number;
}

export interface ProbationEndingSoonResponse {
  as_of: string;
  days_window: number;
  items: ProbationEndingSoonItem[];
}

// ---------- M10.5 org chart types ---------------------------------------

export interface OrgChartAssignee {
  id: string;
  employee_code: string;
  first_name_en: string;
  last_name_en: string;
  first_name_ar: string;
  last_name_ar: string;
}

export interface OrgChartNode {
  id: string;
  position_number: number;
  position_number_display: string;
  title_en: string;
  title_ar: string;
  status: "draft" | "open" | "filled" | "abolished";
  department_name_en: string | null;
  current_assignee: OrgChartAssignee | null;
  children: OrgChartNode[];
}

export interface OrgChartResponse {
  hierarchy_type: string;
  nodes: OrgChartNode[];
}

// ---------- M21.5 user preferences --------------------------------------

export interface UserPreferences {
  // null = "All entities" scope (holding-company view).
  active_legal_entity_id: string | null;
}

// ---------- Department + Branch CRUD types -----------------------------

export interface DepartmentSummary {
  id: string;
  legal_entity_id: string;
  parent_department_id: string | null;
  name_en: string;
  name_ar: string;
  created_at: string;
}

export interface BranchSummary {
  id: string;
  legal_entity_id: string;
  name_en: string;
  name_ar: string;
  city: string | null;
  created_at: string;
}

// ---------- M10.6 Position CRUD types -----------------------------------

export interface JobSummary {
  id: string;
  legal_entity_id: string;
  code: string;
  title_en: string;
  title_ar: string;
  description: string | null;
  mol_number: string | null;
  function_code: string | null;
  sector_type: string;
  job_family: string | null;
  job_type: string;
  full_time_equivalent: string;
  working_hours_per_week: string;
  max_positions: number | null;
  is_supervisory: boolean;
}

export interface PositionAssignee {
  id: string;
  employee_code: string;
  first_name_en: string;
  last_name_en: string;
}

export interface PositionListItem {
  id: string;
  position_number: number;
  position_number_display: string;
  title_en: string;
  title_ar: string;
  status: "draft" | "open" | "filled" | "abolished";
  position_type: string;
  job_id: string;
  job_code: string;
  job_title_en: string;
  department_id: string;
  department_name_en: string;
  current_assignee: PositionAssignee | null;
}

/** Slim employee shape — used by <EmployeePicker /> typeahead AND
 * the People → Employees list page. */
export interface EmployeePickerOption {
  id: string;
  employee_code: string;
  first_name_en: string;
  last_name_en: string;
  first_name_ar: string;
  last_name_ar: string;
  photo_url: string | null;
  employment_status: string;
  is_saudi_national: boolean;
  nationality: string;
  hire_date: string;
  legal_entity_id: string | null;
}

export interface EmployeeDocument {
  id: string;
  employee_id: string;
  category: "contract" | "passport" | "id" | "certificate" | "bank" | "other";
  filename: string;
  content_type: string;
  file_size_bytes: number;
  expiry_date: string | null;
  uploaded_by_clerk_user_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface EmployeeCurrentContract {
  id: string;
  contract_number: number | null;
  contract_status: string | null;
  contract_period: string | null;
  employment_type: string | null;
  probation_end_date: string | null;
}

export interface JobCompetency {
  id: string;
  job_id: string;
  kind: "skill" | "kpi" | "certificate" | "test" | "objective";
  name_en: string;
  name_ar: string | null;
  description: string | null;
  level: string | null;
  importance: string | null;
  weight: string | null;
  measurement: string | null;
  sequence_index: number;
}

// Returned by POST /api/ai-assist/job-full-profile — backs the
// "Generate full profile" button on the Job create dialog +
// detail page.
export interface AiCompetencySkill {
  name_en: string;
  name_ar: string | null;
  description: string | null;
  level: string | null;
  importance: string | null;
}
export interface AiCompetencyKpi {
  name_en: string;
  name_ar: string | null;
  description: string | null;
  measurement: string | null;
  weight: number | null;
}
export interface AiCompetencyCertificate {
  name_en: string;
  name_ar: string | null;
  description: string | null;
  importance: string | null;
}
export interface AiCompetencyTest {
  name_en: string;
  name_ar: string | null;
  description: string | null;
}
export interface AiCompetencyObjective {
  name_en: string;
  name_ar: string | null;
  description: string | null;
  measurement: string | null;
  weight: number | null;
}
export interface AiJobFullProfile {
  code: string;
  title_ar: string;
  job_family: string;
  is_supervisory: boolean;
  description: string;
  skills: AiCompetencySkill[];
  kpis: AiCompetencyKpi[];
  certificates: AiCompetencyCertificate[];
  tests: AiCompetencyTest[];
  objectives: AiCompetencyObjective[];
}

export interface AssignmentHistoryItem {
  id: string;
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  employee_name_ar: string;
  assignment_start: string;
  assignment_end: string | null;
  reason_code: string | null;
  is_current: boolean;
}

export interface ReportsToEdge {
  hierarchy_type_code: string;
  hierarchy_type_name_en: string;
  reports_to_position_id: string;
  reports_to_position_number_display: string;
  reports_to_title_en: string;
  reports_to_title_ar: string;
  effective_from: string;
}

export interface PositionDetail extends PositionListItem {
  description: string | null;
  branch_id: string | null;
  full_time_equivalent: string;
  compensation_region: "west" | "central" | "east";
  is_saudized: boolean;
  allow_delegation: boolean;
  self_approver: boolean;
  activation_date: string | null;
  retirement_date: string | null;
}

export interface PositionCreateBody {
  legal_entity_id: string;
  job_id: string;
  department_id: string;
  branch_id?: string | null;
  grade_id?: string | null;
  title_en: string;
  title_ar: string;
  description?: string | null;
  position_type?: string;
  full_time_equivalent?: string | null;
  compensation_region?: string;
  is_saudized?: boolean;
  allow_delegation?: boolean;
  self_approver?: boolean;
  activation_date?: string | null;
  retirement_date?: string | null;
  status?: string;
}

export interface JobCreateBody {
  legal_entity_id: string;
  code: string;
  title_en: string;
  title_ar: string;
  description?: string | null;
  mol_number?: string | null;
  function_code?: string | null;
  sector_type?: string;
  job_family?: string | null;
  job_type?: string;
  full_time_equivalent?: string;
  working_hours_per_week?: string;
  max_positions?: number | null;
  is_supervisory?: boolean;
}

export interface JobUpdateBody {
  title_en?: string;
  title_ar?: string;
  description?: string | null;
  mol_number?: string | null;
  function_code?: string | null;
  sector_type?: string;
  job_family?: string | null;
  job_type?: string;
  full_time_equivalent?: string;
  working_hours_per_week?: string;
  max_positions?: number | null;
  is_supervisory?: boolean;
}

export interface PositionUpdateBody {
  title_en?: string;
  title_ar?: string;
  description?: string | null;
  position_type?: string;
  full_time_equivalent?: string | null;
  compensation_region?: string;
  is_saudized?: boolean;
  allow_delegation?: boolean;
  self_approver?: boolean;
  activation_date?: string | null;
  retirement_date?: string | null;
  branch_id?: string | null;
  status?: string;
}

// ---------- M15.6/M15.8 leave request types -------------------------------

export interface LeaveRequestDetail {
  id: string;
  employee_id: string;
  // M3X.LIST-AND-REPORT-FIXES PR 2 — pre-joined display fields. Backend
  // populates these from EmployeeORM + LeaveTypeORM in the list query.
  employee_name_en?: string | null;
  employee_name_ar?: string | null;
  employee_code?: string | null;
  legal_entity_id: string;
  leave_type_id: string;
  leave_type_name_en?: string | null;
  leave_type_name_ar?: string | null;
  leave_type_code?: string | null;
  start_date: string;
  end_date: string;
  days_requested: string;
  reason: string | null;
  medical_cert_url: string | null;
  workflow_state: string;
  current_approver_employee_id: string | null;
  approval_step_index: number;
  balance_snapshot_at_submit: string | null;
  denial_reason: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  denied_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowEventEntry {
  id: string;
  workflow_type: string;
  workflow_id: string;
  from_state: string | null;
  to_state: string;
  transition: string;
  actor_clerk_user_id: string | null;
  reason: string | null;
  payload_diff: Record<string, unknown>;
  occurred_at: string;
}

// M17.1 shifts.
// M3X.ATTENDANCE F.6 — biometric machine DTO
export interface BiometricMachineDTO {
  id: string;
  legal_entity_id: string;
  machine_id: string;
  device_type: string;
  location_label: string | null;
  ip_address_vault_key: string | null;
  connection_password_vault_key: string | null;
  connection_mode: string;
  sync_interval_minutes: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error_detail: string | null;
  consecutive_failure_count: number;
  queue_depth: number;
  is_active: boolean;
}

export interface ShiftDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  description: string | null;
  start_time: string;
  end_time: string;
  timezone: string;
  grace_period_minutes: number;
  break_duration_minutes: number;
  days_of_week: boolean[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // M3X.ATTENDANCE F.1 — 18 new knobs
  description_en?: string | null;
  description_ar?: string | null;
  grace_period_minutes_arrival?: number | null;
  grace_period_minutes_departure?: number | null;
  crosses_midnight?: boolean | null;
  is_night_shift?: boolean | null;
  auto_break_after_minutes?: number | null;
  ramadan_start_time?: string | null;
  ramadan_end_time?: string | null;
  ramadan_applies_to_non_muslims?: boolean | null;
  summer_schedule_active?: boolean | null;
  holiday_eve_short_hours?: boolean | null;
  scheduled_minutes_per_day?: number | null;
  scheduled_minutes_per_week?: number | null;
  weekly_rest_day?: string | null;
  count_holidays_as_worked?: boolean | null;
  auto_clock_out_after_minutes?: number | null;
  consider_clock_out_before_shift_end_as_early_departure?: boolean | null;
  min_rest_hours_between_shifts?: number | null;
  max_consecutive_working_days?: number | null;
  gender_restriction?: string | null;
  applicable_for_juveniles?: boolean | null;
}

// M17.2 attendance.
export interface AttendanceDetail {
  id: string;
  employee_id: string;
  attendance_date: string;
  clock_in_time: string | null;
  clock_out_time: string | null;
  source: string;
  expected_shift_id: string | null;
  late_minutes: number;
  early_departure_minutes: number;
  overtime_minutes: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// M17.6 excuse types.
export interface ExcuseTypeDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  description: string | null;
  excuse_category: string;
  max_per_month: number | null;
  max_per_year: number | null;
  requires_attachment: boolean;
  paid: boolean;
  affects_attendance: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// M18.8 termination workflow.
export interface TerminationSummary {
  id: string;
  employee_id: string;
  // M3X.LIST-AND-REPORT-FIXES PR 2 — pre-joined display fields.
  employee_name_en?: string | null;
  employee_name_ar?: string | null;
  employee_code?: string | null;
  legal_entity_id: string;
  source: string;
  termination_type: string;
  termination_reason_code: string | null;
  termination_reason: string | null;
  notice_date: string | null;
  effective_date: string;
  last_working_day: string | null;
  workflow_state: string;
  current_approver_employee_id: string | null;
  approval_step_index: number;
  eos_calculation_id: string | null;
  final_settlement_id: string | null;
  final_payroll_run_id: string | null;
  initiated_at: string | null;
  settlement_calculated_at: string | null;
  approved_at: string | null;
  closed_at: string | null;
  denied_at: string | null;
  cancelled_at: string | null;
  denial_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface EosSnapshot {
  id: string;
  calculation_date: string;
  termination_type: string;
  last_working_day: string;
  service_years: string;
  service_months: number;
  eos_amount: string;
  eos_currency: string;
  audit_trace: Record<string, unknown>;
}

export interface FinalSettlementSnapshot {
  id: string;
  leave_encashment_amount: string;
  outstanding_loan_balance: string;
  pending_allowances: string;
  pending_deductions: string;
  ticket_entitlement_value: string;
  gratuity_other: string;
  total_settlement_amount: string;
  currency: string;
  status: string;
}

export interface TerminationDetail extends TerminationSummary {
  eos: EosSnapshot | null;
  settlement: FinalSettlementSnapshot | null;
}

export interface ExitClearanceItem {
  id: string;
  exit_clearance_id: string;
  item_type: string;
  description: string;
  assigned_to_employee_id: string | null;
  status: string;
  completed_at: string | null;
  completed_by_clerk_user_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExitClearanceSummary {
  id: string;
  termination_id: string;
  employee_id: string;
  legal_entity_id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExitClearanceFull {
  clearance: ExitClearanceSummary | null;
  items: ExitClearanceItem[];
  is_complete: boolean;
}

export interface GovernmentFilingDetail {
  id: string;
  termination_id: string;
  employee_id: string;
  legal_entity_id: string;
  filing_type: string;
  status: string;
  payload_json: Record<string, unknown>;
  errors: Record<string, unknown>;
  submitted_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  created_at: string;
  updated_at: string;
}

// M30 P8 — HR Operations Hub aggregator response (M30.5.1 consumer).
//
// The hub endpoint returns the 7-card grid + recent HR-initiated
// activity feed + pending PayrollRunCandidate count. Backend lives
// at api/routes/transactions/hub.py.
//
// One field of note: `display_en` / `display_ar` ride on the
// payload so the hub renders bilingual without per-tenant config.
// The page passes them to <LtrText> when needed (codes are
// machine-stable; labels switch on locale).
export interface HubTransactionTypeCard {
  transaction_type:
    | "leave"
    | "loan"
    | "excuse"
    | "attendance"
    | "overtime"
    | "eos"
    | "contract_amendment";
  display_en: string;
  display_ar: string;
  pending_count: number;
  recent_7d_commit_count: number;
  detail_route: string;
  create_route: string;
}

export interface HubActivityItem {
  transaction_type: string;
  transaction_id: string;
  employee_id: string;
  actor_clerk_user_id: string | null;
  workflow_state: string | null;
  created_at: string;
}

export interface HubAggregate {
  per_type: HubTransactionTypeCard[];
  recent_hr_initiated_activity: HubActivityItem[];
  pending_payroll_candidates: number;
}

// M30.5.1 PayrollRunCandidate list-view row.
export interface PayrollCandidateRow {
  id: string;
  transaction_type: string;
  transaction_id: string;
  employee_id: string;
  legal_entity_id: string | null;
  target_period_year_month: string;
  line_item_type: string;
  amount: string; // Decimal serialized as string
  currency: string;
  description_en: string;
  description_ar: string;
  source_reason_code: string | null;
  status: string;
  queued_at: string;
  included_in_run_id: string | null;
  failed_reason: string | null;
}

export interface PayrollCandidatesListResponse {
  items: PayrollCandidateRow[];
  total: number;
}

// M30.5.3 — reason-codes list response.
export interface ReasonCodeOut {
  code: string;
  display_en: string;
  display_ar: string;
  labor_law_articles: number[];
  requires_supporting_document: boolean;
  requires_hr_explanation: boolean;
  payroll_effects: "none" | "forfeiture" | "multiplier" | "deduction";
  behavioral_implications: string;
}
export interface ReasonCodesListResponse {
  transaction_type: string;
  codes: ReasonCodeOut[];
}

// M30.5.3 — shared transaction-assist response shape.
export interface AssistPanelFindingOut {
  panel: string;
  severity: "error" | "warning" | "info";
  code: string;
  message: string;
  field_ref: string | null;
  suggested_value: unknown;
  citation: string | null;
}

export interface AssistPanelOut {
  panel: string;
  findings: AssistPanelFindingOut[];
}

export interface PayrollLineItemPreview {
  line_item_type: string;
  amount: string;
  currency: string;
  description_en: string;
  description_ar: string;
  target_period_year_month: string;
}

export interface TransactionAssistOut {
  transaction_type: string;
  reason_code: ReasonCodeOut;
  panels: AssistPanelOut[];
  preview_payroll_entries: PayrollLineItemPreview[];
  has_blocking_errors: boolean;
}

// M30.5.3 — per-type submit body shapes.
export interface LeaveHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  leave_type_code: string;
  start_date: string;
  end_date: string;
  days_requested?: string | null;
  is_unpaid_override?: boolean | null;
}

export interface LeaveHrInitiateCommitOut {
  leave_request_id: string;
  workflow_state: string;
  payroll_candidates_queued: number;
}

export interface LoanHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  loan_product_code: string;
  requested_amount: string;
  requested_tenure_months: number;
  purpose?: string | null;
  disbursement_date?: string | null;
}

export interface LoanHrInitiateCommitOut {
  loan_application_id: string;
  workflow_state: string;
  installments_scheduled: number;
  payroll_candidates_queued: number;
}

export interface ExcuseHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  excuse_type_code: string;
  attendance_date: string;
  duration_minutes?: number | null;
  attachment_url?: string | null;
  related_attendance_record_id?: string | null;
}

export interface ExcuseHrInitiateCommitOut {
  excuse_request_id: string;
  workflow_state: string;
  payroll_candidates_queued: number;
}

// M30.5.4 — T&A.
export interface AttendanceHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  attendance_date: string;
  clock_in_time?: string | null;
  clock_out_time?: string | null;
  expected_shift_id?: string | null;
  notes?: string | null;
}
export interface AttendanceHrInitiateCommitOut {
  attendance_record_id: string;
  attendance_date: string;
  was_overwrite: boolean;
}

// M30.5.4 — Overtime.
export interface OvertimeHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  attendance_date: string;
  overtime_minutes: number;
  notes?: string | null;
}
export interface OvertimeHrInitiateCommitOut {
  attendance_record_id: string;
  attendance_date: string;
  overtime_minutes: number;
  payroll_candidates_queued: number;
  earning_amount: string;
}

// M30.5.4 — Contract Amendment.
export interface ContractAmendmentHrInitiateBody {
  actor_user_id: string;
  actor_role: string;
  target_employee_id: string;
  reason_code: string;
  reason_explanation?: string | null;
  supporting_document_url?: string | null;
  ai_findings_acknowledged?: string[];
  effective_date: string;
  proposed_basic_salary?: string | null;
  proposed_housing_allowance?: string | null;
  proposed_transportation_allowance?: string | null;
  proposed_food_allowance?: string | null;
  proposed_other_fixed_allowances?: string | null;
  proposed_probation_period_days?: number | null;
  proposed_contract_type?: string | null;
  proposed_employment_type?: string | null;
  employee_consent_to_decrease?: boolean;
  employee_consent_to_probation_extension?: boolean;
}

// M20.1 Mission Control headlines.
export interface MissionControlHeadlineCard {
  key: string;
  label: string;
  count: number;
  link_to: string;
  sentiment: "neutral" | "attention" | "warning" | "critical";
  /** Smaller secondary line under the count (e.g. the headcount
   *  card's "N total incl. terminated"). */
  secondary: string | null;
  /** 7-day created-per-day trend for the KPI-card sparkline
   *  (oldest → newest). Null where a trend isn't meaningful. */
  trend: number[] | null;
}

export interface MissionControlHeadlines {
  cards: MissionControlHeadlineCard[];
  compliance_breakdown: Record<string, number>;
}

// ---------- M23.2 Chat drafts -------------------------------------------

export interface ChatDraft {
  id: string;
  session_id: string | null;
  clerk_user_id: string;
  kind: string;
  title: string;
  content: string;
  status: "pending" | "accepted" | "discarded";
  reviewed_by_clerk_user_id: string | null;
  reviewed_at: string | null;
  created_at: string;
}

// M24 — the result of applying a parked chat mutation.
export interface ChatConfirmResult {
  confirmation_id: string;
  status: string;
  tool_name: string;
  summary: string;
  render: string;
  data: Record<string, unknown>;
  audit_event_id: string | null;
}

// M25.3 — a pinned chat conversation.
export interface ChatThreadSummary {
  id: string;
  name: string;
  message_count: number;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChatThread extends ChatThreadSummary {
  transcript: Record<string, unknown>[];
  share_code: string | null;
}

// M25.6 — the read-only view of a shared thread (no owner / controls).
export interface SharedThread {
  name: string;
  message_count: number;
  transcript: Record<string, unknown>[];
  created_at: string;
}

// Chat feedback (👍 / 👎 + optional comment).
export interface ChatFeedback {
  id: string;
  session_id: string;
  turn_id: string;
  sentiment: "up" | "down";
  comment: string | null;
  clerk_user_id: string;
  message_snippet: string | null;
  created_at: string;
}

export interface ChatFeedbackListResponse {
  items: ChatFeedback[];
  total_up: number;
  total_down: number;
}

// ---------- M21.10 AI Status + Global Search ----------------------------

export interface AiStatus {
  status: "healthy" | "paused";
  autonomy_mode: "conservative" | "standard" | "aggressive";
  paused: boolean;
  actions_today: number;
  escalations_today: number;
}

export interface GlobalSearchItem {
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

export interface GlobalSearchGroup {
  kind: "people" | "policies" | "settings" | "transactions";
  label: string;
  items: GlobalSearchItem[];
}

export interface GlobalSearchInterpreted {
  label: string;
  description: string;
  link: string;
}

export interface GlobalSearchResponse {
  query: string;
  interpreted: GlobalSearchInterpreted | null;
  groups: GlobalSearchGroup[];
}

// ---------- M21.15 Report dashboards + audit log ------------------------

export interface ReportSeries {
  label: string;
  value: number;
}

export interface HeadcountReport {
  active_total: number;
  grand_total: number;
  by_status: ReportSeries[];
  by_nationality: ReportSeries[];
  hires_by_month: ReportSeries[];
}

export interface TurnoverReport {
  leaver_count: number;
  avg_tenure_years: number;
  terminations_by_month: ReportSeries[];
}

export interface CompensationReport {
  total_monthly_basic: number;
  contract_count: number;
  by_salary_band: ReportSeries[];
}

export interface ComplianceReport {
  iqama_expiring_90d: number;
  contract_expiring_90d: number;
  probation_ending_30d: number;
  saudization: ReportSeries[];
}

export interface AuditEntry {
  source: string;
  event_type: string;
  actor: string | null;
  summary: string;
  reference_id: string;
  occurred_at: string;
  // M24.5 — only populated for `chat` mutation entries.
  entry_id: string | null;
  reversible: boolean;
  reversed: boolean;
  // Phase 4 — populated for chat rows the same actor thumbed
  // within ±2 minutes of the audit row. Null otherwise.
  feedback_sentiment?: "up" | "down" | null;
  feedback_comment?: string | null;
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ---------- M21.14 Statutory defaults -----------------------------------

export interface StatutoryItem {
  key: string;
  label: string;
  statutory_value: string;
  citation: string;
  note: string;
}

export interface StatutoryDefaultsResponse {
  domain: string;
  items: StatutoryItem[];
}

// ---------- M21.13 Approval Chain Configurator --------------------------

export interface ApprovalChainStep {
  step_number: number;
  kind: "immediate_manager" | "position" | "employee";
  position_id?: string | null;
  employee_id?: string | null;
  guard_expression?: string | null;
  sla_hours?: number | null;
  parallel?: boolean;
}

export interface ApprovalChain {
  id: string;
  legal_entity_id: string;
  workflow_type: string;
  steps: ApprovalChainStep[];
  notes: string | null;
  is_active: boolean;
  updated_at: string;
}

export interface ResolvedApprovalStep {
  step_index: number;
  step_kind: string;
  resolved: boolean;
  employee_id: string | null;
  employee_name_en: string | null;
  failure_reason: string | null;
}

export interface AiTranslateResponse {
  translation: string;
}

export interface AiGenerateResponse {
  draft: string;
  prompt_name: string;
  prompt_version: number;
  model: string;
}

export interface PendingApprovalItem {
  id: string;
  workflow_type:
    | "leave_request"
    | "loan_application"
    | "excuse_request"
    | "termination";
  employee_id: string;
  legal_entity_id: string;
  current_approver_employee_id: string | null;
  summary: string;
  state: string;
  created_at: string;
  detail_link: string;
  /** Stamped in by the endpoint after a batched employee lookup —
   *  the escalation cards render a person, not a bare UUID. */
  employee_name_en: string | null;
  employee_code: string | null;
}

export interface ComplianceAlertItem {
  kind: "iqama_expiry" | "contract_expiry" | "probation_ending";
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  legal_entity_id: string;
  due_date: string;
  days_until: number;
  detail_link: string;
}

export interface ComplianceAlertsResponse {
  alerts: ComplianceAlertItem[];
  counts: Record<string, number>;
}

export interface AnomalyAlert {
  kind: "leave_burst" | "attendance_override_burst";
  severity: "info" | "warning" | "critical";
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  legal_entity_id: string | null;
  summary: string;
  detail_link: string;
  count: number;
}

export interface AnomaliesResponse {
  anomalies: AnomalyAlert[];
  counts: Record<string, number>;
}

export interface AiActivityItem {
  id: string;
  workflow_type: string;
  workflow_id: string;
  from_state: string | null;
  to_state: string;
  transition: string;
  actor: string;
  reason: string | null;
  occurred_at: string;
}

export interface AiActivityResponse {
  summary: {
    handled_count: number;
    escalated_count: number;
    distinct_workflows: number;
  };
  items: AiActivityItem[];
}

export interface ActiveApprover {
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  first_name_en: string;
  last_name_en: string;
  photo_url: string | null;
  pending_total: number;
}

export interface ActiveApproversResponse {
  approvers: ActiveApprover[];
}

// M19 AI policy ingestion.
export interface PolicyExtractionDraftDetail {
  id: string;
  legal_entity_id: string;
  knowledge_document_id: string;
  status: string;
  extracted_domains: string[];
  extraction_confidence_overall: string | null;
  page_count: number;
  reviewer_clerk_user_id: string | null;
  reviewed_at: string | null;
  extraction_started_at: string | null;
  extraction_completed_at: string | null;
  errors: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PolicyExtractedItem {
  id: string;
  draft_id: string;
  domain: string;
  sequence_index: number;
  payload_json: Record<string, unknown>;
  field_confidences: Record<string, string>;
  source_pages: number[];
  overall_confidence: string | null;
  approval_status: string;
  approved_at: string | null;
  approved_by_clerk_user_id: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  live_entity_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PolicyExtractionDraftFull {
  draft: PolicyExtractionDraftDetail;
  items: PolicyExtractedItem[];
  items_by_domain: Record<string, PolicyExtractedItem[]>;
}

export interface PolicyDraftDiff {
  items: Array<{
    id: string;
    domain: string;
    diff_status: "new" | "changed" | "unchanged";
    field_diffs: Record<string, { live: unknown; draft: unknown }> | null;
  }>;
  summary: Record<"new" | "changed" | "unchanged", number>;
  by_domain: Record<string, Record<"new" | "changed" | "unchanged", number>>;
}

export interface PromoteSummary {
  promoted: Record<string, number>;
  skipped_pending: number;
  skipped_rejected: number;
  promoted_total: number;
  failed: number;
  errors: Array<{ item_id: string; domain: string; message: string }>;
}

// M17.7-17.9 excuse requests.
export interface ExcuseRequestDetail {
  id: string;
  employee_id: string;
  legal_entity_id: string;
  excuse_type_id: string;
  attendance_date: string;
  reason: string;
  attachment_url: string | null;
  workflow_state: string;
  current_approver_employee_id: string | null;
  approval_step_index: number;
  related_attendance_record_id: string | null;
  denial_reason: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  recorded_at: string | null;
  denied_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

// ---------- M16 loan types -----------------------------------------------

export interface LoanProductDetail {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  description: string | null;
  max_amount: string;
  max_tenure_months: number;
  interest_rate: string;
  currency: string;
  min_service_months_eligibility: number;
  max_concurrent_loans_per_employee: number;
  eligibility_rule_id: string | null;
  repayment_frequency: string;
  early_settlement_allowed: boolean;
  product_status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;

  // M3X — extended catalog knobs surfaced on detail page
  sharia_structure_type?: string | null;
  is_car_loan?: boolean | null;
  car_loan_percentage?: string | null;
  bond_max_years?: number | null;
  bond_pro_ration_formula?: string | null;

  ai_auto_approval_threshold_amount?: string | null;
  ai_can_propose_refinance?: boolean | null;
  ai_can_propose_tenure_extension?: boolean | null;
  ai_default_risk_scoring_enabled?: boolean | null;
  ai_proactive_outreach_enabled?: boolean | null;

  min_settlement_months?: number | null;
  min_settlement_allowable_amount?: string | null;
  max_settlement_allowable_amount?: string | null;
  early_settlement_discount?: string | null;
  early_settlement_penalty?: string | null;
  allow_installment_change?: boolean | null;
  formula_multiplier?: string | null;
  min_value?: string | null;
  user_can_change_calculated_amount?: boolean | null;
  follow_formula_on_loan_amount?: boolean | null;

  loan_during_probation?: string | null;
  loan_valid_for_nationalities?: string | null;
  iqama_tenure_buffer_days?: number | null;
  eligible_after_months?: number | null;
  eligible_from?: string | null;
  allow_with_other_loans_same_type?: boolean | null;
  allow_with_other_loans_any_type?: boolean | null;
  is_paid_with_leave?: boolean | null;
  repayment_during_unpaid_leave?: string | null;
  repayment_during_reduced_pay_leave?: string | null;

  guarantor_required_for_all?: boolean | null;
  guarantor_required_above_amount?: string | null;
  guarantor_min_grade?: string | null;
  eos_compensation_percentage?: string | null;
  value_should_not_exceed_eos_compensation?: boolean | null;
  validate_loan_amount_against_eos_compensation?: boolean | null;
  external_beneficiary_routing?: string | null;
}

// M3X — payoff quote DTO
// M3X.EOS F.3 — Notice-period sub-workflow DTO
export interface NoticePeriodDTO {
  id: string;
  termination_id: string;
  sub_state: string;
  notice_start_date: string;
  last_working_day: string;
  exit_interview_completed_at: string | null;
  exit_interview_synthesis: Record<string, unknown> | null;
  handover_complete_at: string | null;
  state_transitions: Array<Record<string, unknown>>;
}

// M3X.EOS F.4 + F.5 — Exit interview synthesis DTO
export interface ExitInterviewSynthesisDTO {
  executive_summary_en: string;
  executive_summary_ar: string;
  root_cause_tags: string[];
  confidence_per_tag: Record<string, number>;
  recommended_actions_en: string[];
  recommended_actions_ar: string[];
  source: string;
}

// M3X.EOS F.21 — Onboarding auto-flush DTOs
export interface EosAutoFlushCellResultDTO {
  trigger_type: string;
  tier_label: string;
  min_years: number;
  max_years: number;
  percent: string;
  extractor_confidence: string;
  source_quote: string;
  statutory_floor_percent: string;
  statutory_floor_citation: string;
  status: "auto_committed" | "pending_hr_review" | "mutabaa_vetoed";
  veto_reason: string | null;
  committed_id: string | null;
}

export interface EosAutoFlushResultDTO {
  auto_committed_count: number;
  pending_review_count: number;
  vetoed_count: number;
  total_proposed: number;
  per_cell: EosAutoFlushCellResultDTO[];
}

// M3X.EOS F.6 — Article 80 classification DTOs
export interface Article80Subtype {
  code: string;
  label_en: string;
  label_ar: string;
  evidence_required: string[];
}

export interface Article80CandidateSubtype {
  code: string;
  label_en: string;
  label_ar: string;
  confidence: string;
}

export interface Article80ClassifyResult {
  suggested_subtype_code: string;
  suggested_subtype: Article80Subtype;
  confidence: string;
  candidate_subtypes: Article80CandidateSubtype[];
  reasoning: string;
  evidence_required: string[];
  source: string;
}

// M3X.EOS F.15 — Settlement breakdown DTOs
export interface SettlementComponentDTO {
  name: string;
  label_en: string;
  label_ar: string;
  amount: string;
  is_deduction: boolean;
  source_citation: string;
  audit_trace: Record<string, unknown>;
}

export interface SettlementBreakdownDTO {
  components: SettlementComponentDTO[];
  earnings_subtotal: string;
  deductions_subtotal: string;
  net_settlement: string;
  currency: string;
  trigger_type: string;
  rate_matrix_tier_label: string | null;
  rate_matrix_percent: string | null;
  statutory_floor_percent: string | null;
  is_disputed: boolean;
}

// M3X.EOS §1.2 — Rate matrix DTOs
export interface EosRateMatrixCellDTO {
  id: string;
  legal_entity_id: string;
  trigger_type: string;
  tier_label: string;
  min_years: number;
  max_years: number;
  percent: string;
  statutory_floor_percent: string;
  jurisdiction_code: string;
}

export interface EosFloorRefDTO {
  trigger_type: string;
  tier_label: string;
  floor_percent: string;
  article_citation: string;
  jurisdiction: string;
}

export interface PayoffQuoteDTO {
  outstanding_principal: string;
  discount_amount: string;
  penalty_amount: string;
  payoff_total: string;
  currency: string;
  valid_until: string;
  reasoning: string;
}

// M3X — refinance proposal DTO
export interface RefinanceProposalDTO {
  new_amount: string;
  new_tenure_months: number;
  new_monthly_installment: string;
  consolidated_from: string[];
  total_savings_monthly: string;
  confidence: number;
  reasoning: string;
}

// M3X — denial explanation
export interface DenialExplanationDTO {
  reason_code: string;
  reason_en: string;
  reason_ar: string;
  counterfactual_en: string | null;
  counterfactual_ar: string | null;
  appeal_available: boolean;
  appeal_path: string | null;
}

// M3X — loan suspension DTO
export interface SuspensionDTO {
  loan_id: string;
  policy_applied: string;
  installments_paused: number;
  new_end_date: string | null;
  new_workflow_state: string;
  reason: string;
}

export interface LoanApplicationDetail {
  id: string;
  employee_id: string;
  legal_entity_id: string;
  loan_product_id: string;
  requested_amount: string;
  requested_tenure_months: number;
  purpose: string | null;
  currency: string;
  approved_amount: string | null;
  approved_tenure_months: number | null;
  monthly_installment: string | null;
  workflow_state: string;
  current_approver_employee_id: string | null;
  approval_step_index: number;
  eligibility_reason: string | null;
  denial_reason: string | null;
  submitted_at: string | null;
  eligibility_checked_at: string | null;
  approved_at: string | null;
  disbursed_at: string | null;
  disbursement_date: string | null;
  settled_at: string | null;
  denied_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LoanInstallmentEntry {
  id: string;
  loan_application_id: string;
  sequence_no: number;
  due_date: string;
  amount: string;
  currency: string;
  status: string;
  payroll_run_id: string | null;
  deducted_amount: string | null;
  deducted_at: string | null;
}


// ---------- M29 Import Framework (Phase F) ---------------------------
//
// PR 1 ships only what the sessions endpoint actually returns.
// Richer DTOs land in subsequent PRs as the surface grows.

export interface ImportFileMetadata {
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sheet_count: number;
  rows_per_sheet: Array<{
    sheet_name: string;
    row_count: number;
    headers: string[];
  }>;
  blob_url: string | null;
}

export interface ImportSessionDTO {
  id: string;
  legal_entity_id: string | null;
  initiated_by_clerk_user_id: string | null;
  status: string;
  mode: string;
  file_metadata: ImportFileMetadata[];
  total_files: number;
  total_rows_parsed: number;
  total_rows_committed: number;
  total_rows_pending: number;
  ai_cost_so_far_usd: number;
  ai_budget_usd: number;
  dashboard_state: Record<string, unknown>;
  conversation_id: string | null;
  failure_reason: string | null;
  uploaded_at: string;
  completed_at: string | null;
}

export interface ImportProposalDTO {
  id: string;
  import_session_id: string;
  kind: "cleansing" | "mapping" | "new_config";
  target_entity_type: string | null;
  payload: Record<string, unknown>;
  confidence: number;
  reasoning: string | null;
  source_row_ids: string[];
  status: "proposed" | "approved" | "edited" | "rejected" | "applied";
  sequence_index: number;
  reviewer_clerk_user_id: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_entity_id: string | null;
}

export interface ImportPendingRowDTO {
  id: string;
  import_session_id: string;
  import_row_id: string;
  entity_type: string;
  mapped_data: Record<string, unknown>;
  validation_messages: Array<{
    level: string;
    message_en: string;
    message_ar: string | null;
    field: string | null;
    suggested_fix: string | null;
  }>;
  status: "pending" | "fixed" | "discarded" | "expired";
  expires_at: string;
  warned_day_21_at: string | null;
  warned_day_28_at: string | null;
  created_at: string;
}

export interface ImportFailureDTO {
  id: string;
  import_session_id: string;
  failure_kind: string;
  message_en: string;
  message_ar: string | null;
  affected_file_index: number | null;
  affected_sheet_name: string | null;
  suggested_actions: Array<{
    action: string;
    label_en: string;
    label_ar: string;
    href: string;
  }>;
  created_at: string;
}


